/**
 * @module engine/shapes/drawFns
 * @summary The ~100 procedural Canvas-2D draw functions, one per shape type.
 * @description
 *   This is the visual heart of the tool — each function paints one piece of
 *   process equipment (silo, twin-screw extruder, cyclone, valve, …) using the
 *   raw Canvas 2D API. They are ported VERBATIM from the original tool so the
 *   rendering is pixel-identical; only typed signatures were added.
 *
 *   Contract: `drawFn(c, s)` first calls `setCtx` to apply the shape's
 *   fill/stroke/lineWidth, then dispatches to `D[s.type]`. Each `D[...]`
 *   function draws in WORLD coordinates using the shape's own x/y/w/h — it must
 *   NOT read engine globals (zoom/pan); the caller has already applied the view
 *   transform. // INVARIANT: D functions depend only on (c, s) and Math.
 *
 * @ported-from JCC PFD Engineering Tool v1.1.html (D object, verbatim).
 */
import type { DrawFn, Shape } from '../../types';
// EXCEPTION to the "pure (c,s) only" invariant: the `titleblock` symbol below
// renders live document metadata (name/rev/date), so it reads S.docMeta.
import { S } from '../state';
// Shared with the port-anchor table so an elbow's art and its connection points
// are derived from the same number.
import { elbowStub } from './ports';

// ─── Apply a shape's style to the context before its draw fn runs ───
export function setCtx(c: CanvasRenderingContext2D, s: Shape): void {
  c.strokeStyle = s.strokeColor || '#1a1a1a';
  c.fillStyle = s.fillColor || '#ffffff';
  c.lineWidth = s.strokeWidth || 1.5;
}

// ═══════════════════════════ DRAW FUNCTIONS (verbatim) ═══════════════════════════
export const D: Record<string, DrawFn> = {
  silo(c,s){const{x,y,w,h}=s,bH=h*.78;
    c.fillRect(x,y,w,bH);c.strokeRect(x,y,w,bH);
    c.beginPath();c.ellipse(x+w/2,y,w/2,5,0,0,Math.PI*2);c.stroke();
    const og=c.fillStyle;c.fillStyle='rgba(0,0,0,0.05)';c.fillRect(x+1,y+bH*.35,w-2,bH*.65);c.fillStyle=og;
    c.beginPath();c.moveTo(x,y+bH);c.lineTo(x+w/2,y+h);c.lineTo(x+w,y+bH);c.closePath();c.fill();c.stroke();},
  hopper(c,s){const{x,y,w,h}=s,bw=w*.28,bH=h*.72;
    c.beginPath();c.moveTo(x,y);c.lineTo(x+w,y);c.lineTo(x+w/2+bw/2,y+bH);c.lineTo(x+w/2-bw/2,y+bH);c.closePath();c.fill();c.stroke();
    c.fillRect(x+w/2-bw/2,y+bH,bw,h-bH);c.strokeRect(x+w/2-bw/2,y+bH,bw,h-bH);},
  htank(c,s){const{x,y,w,h}=s,r=h*.4;
    c.fillRect(x+r,y,w-r*2,h);c.strokeRect(x+r,y,w-r*2,h);
    c.beginPath();c.ellipse(x+r,y+h/2,r*.38,h/2,0,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.ellipse(x+w-r,y+h/2,r*.38,h/2,0,0,Math.PI*2);c.fill();c.stroke();},
  vtank(c,s){const{x,y,w,h}=s,r=w*.18;
    c.fillRect(x,y+r,w,h-r*2);c.strokeRect(x,y+r,w,h-r*2);
    c.beginPath();c.ellipse(x+w/2,y+r,w/2,r,0,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.ellipse(x+w/2,y+h-r,w/2,r,0,0,Math.PI*2);c.fill();c.stroke();},
  bulkbag(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y+h*.15,w,h*.75);c.strokeRect(x,y+h*.15,w,h*.75);
    c.beginPath();c.moveTo(x+w*.15,y);c.lineTo(x+w*.5,y+h*.15);c.moveTo(x+w*.85,y);c.lineTo(x+w*.5,y+h*.15);c.moveTo(x+w*.5,y);c.lineTo(x+w*.5,y+h*.15);c.stroke();
    c.beginPath();c.moveTo(x+w*.2,y+h*.9);c.lineTo(x+w*.5,y+h);c.lineTo(x+w*.8,y+h*.9);c.stroke();},
  ibc(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h*.8);c.strokeRect(x,y,w,h*.8);
    c.fillRect(x+w*.1,y+h*.8,w*.8,h*.2);c.strokeRect(x+w*.1,y+h*.8,w*.8,h*.2);
    for(let i=1;i<4;i++){c.beginPath();c.moveTo(x+w*i/4,y);c.lineTo(x+w*i/4,y+h*.8);c.globalAlpha=.15;c.stroke();c.globalAlpha=1;}},
  rotaryvalve(c,s){const{x,y,w,h}=s,cx=x+w/2,cy=y+h/2,r=Math.min(w,h)*.38;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    for(let i=0;i<4;i++){const a=i*Math.PI/2+Math.PI/4;c.beginPath();c.moveTo(cx,cy);c.lineTo(cx+Math.cos(a)*r,cy+Math.sin(a)*r);c.stroke();}
    c.beginPath();c.arc(cx,cy,2.5,0,Math.PI*2);c.fill();},
  liwfeeder(c,s){
    // LIW Feeder: large conical hopper + load cell platform + screw discharge
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;

    // ── Flat top rim / dust cover ──
    c.fillRect(x+w*.08,y,w*.84,h*.07);
    c.strokeRect(x+w*.08,y,w*.84,h*.07);

    // ── Two small inlet nozzles on top rim ──
    [x+w*.28,x+w*.72].forEach(nx=>{
      c.fillRect(nx-4,y-7,8,8);c.strokeRect(nx-4,y-7,8,8);
      c.beginPath();c.arc(nx,y-10,3.5,0,Math.PI*2);c.fill();c.stroke();
    });

    // ── Main conical hopper body ──
    // Wide top trapezoid body
    const topW=w*.84,botW=w*.28,topY=y+h*.07,midY=y+h*.5;
    const tl=x+(w-topW)/2, tr=tl+topW;
    const bl=x+(w-botW)/2, br=bl+botW;
    c.beginPath();
    c.moveTo(tl,topY);c.lineTo(tr,topY);c.lineTo(br,midY);c.lineTo(bl,midY);
    c.closePath();c.fill();c.stroke();

    // Material fill inside hopper (light grey tint)
    c.fillStyle='rgba(0,0,0,0.06)';
    c.beginPath();
    c.moveTo(tl+2,topY+2);c.lineTo(tr-2,topY+2);c.lineTo(br-2,midY-2);c.lineTo(bl+2,midY-2);
    c.closePath();c.fill();
    c.fillStyle=s.fillColor||'#ffffff';

    // ── Load cell platform (horizontal bar) ──
    c.fillRect(x,midY,w,h*.1);
    c.strokeRect(x,midY,w,h*.1);

    // Load cells (4 cylinders on platform corners)
    [[x+w*.12,midY+h*.1],[x+w*.88,midY+h*.1]].forEach(([px,py])=>{
      c.fillRect(px-5,py,10,h*.1);c.strokeRect(px-5,py,10,h*.1);
      // Load cell foot
      c.beginPath();c.arc(px,py+h*.11,6,0,Math.PI*2);
      c.fillStyle='rgba(0,0,0,0.15)';c.fill();c.stroke();
      c.fillStyle=s.fillColor||'#ffffff';
    });

    // ── Cylinder lower section (below platform) ──
    const cylY=midY+h*.2,cylH=h*.18;
    c.fillRect(bl,cylY,botW,cylH);c.strokeRect(bl,cylY,botW,cylH);
    c.beginPath();c.ellipse(CX,cylY,botW/2,h*.03,0,0,Math.PI*2);c.fill();c.stroke();

    // ── Ball valve discharge ──
    const valveY=cylY+cylH,valveR=botW*.4;
    c.beginPath();c.arc(CX,valveY+valveR*.8,valveR,0,Math.PI*2);
    c.fillStyle='rgba(0,0,0,0.1)';c.fill();c.stroke();
    c.fillStyle=s.fillColor||'#ffffff';
    // valve cross
    c.lineWidth=(s.strokeWidth||1.5)*1.3;
    c.beginPath();c.moveTo(CX-valveR*.6,valveY+valveR*.8);c.lineTo(CX+valveR*.6,valveY+valveR*.8);c.stroke();
    c.beginPath();c.moveTo(CX,valveY+valveR*.2);c.lineTo(CX,valveY+valveR*1.4);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;

    // ── Screw conveyor outlet (horizontal, right side) ──
    const screwY=y+h*.9;
    const screwX=x+w*.45,screwW=w*.7,screwH=h*.11;
    c.fillRect(screwX,screwY,screwW,screwH);c.strokeRect(screwX,screwY,screwW,screwH);
    // helix
    c.beginPath();
    for(let i=0;i<=screwW;i+=2){
      const hy=screwY+screwH/2+Math.sin(i/screwW*Math.PI*5)*(screwH*.4);
      i===0?c.moveTo(screwX+i,hy):c.lineTo(screwX+i,hy);
    }
    c.globalAlpha=.35;c.stroke();c.globalAlpha=1;
    // outlet arrow
    c.beginPath();c.moveTo(screwX+screwW,screwY+screwH/2);c.lineTo(screwX+screwW+8,screwY+screwH/2);c.stroke();

    // ── Connecting pipe from valve to screw ──
    c.beginPath();c.moveTo(CX,valveY+valveR*1.6);c.lineTo(CX,screwY+screwH/2);c.lineTo(screwX,screwY+screwH/2);c.stroke();

    c.fillStyle=og;},
  volfeeder(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h*.6);c.strokeRect(x,y,w,h*.6);
    c.beginPath();c.moveTo(x,y+h*.6);c.lineTo(x+w/2,y+h*.85);c.lineTo(x+w,y+h*.6);c.stroke();
    c.beginPath();c.moveTo(x+w/2,y+h*.85);c.lineTo(x+w/2,y+h);c.stroke();},
  screwconv(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();for(let i=0;i<=w;i+=2){const py=y+h/2+Math.sin(i/w*Math.PI*8)*(h*.3);i===0?c.moveTo(x+i,py):c.lineTo(x+i,py);}
    c.globalAlpha=.35;c.stroke();c.globalAlpha=1;},
  beltconv(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();c.arc(x+10,y+h/2,h/2-1,Math.PI/2,Math.PI*1.5);c.stroke();
    c.beginPath();c.arc(x+w-10,y+h/2,h/2-1,-Math.PI/2,Math.PI/2);c.stroke();},
  bagdump(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y+h*.3,w,h*.7);c.strokeRect(x,y+h*.3,w,h*.7);
    c.beginPath();c.moveTo(x+w*.15,y);c.lineTo(x+w*.85,y);c.lineTo(x+w,y+h*.3);c.lineTo(x,y+h*.3);c.closePath();c.fill();c.stroke();
    const og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';c.font='8px Segoe UI';c.textAlign='center';c.fillText('BAG',x+w/2,y+h*.65);c.fillStyle=og;},
  tse(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(x-14,y-3,15,h+6);c.strokeRect(x-14,y-3,15,h+6);
    c.beginPath();c.arc(x-23,y+h/2,10,0,Math.PI*2);c.fill();c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 8px Segoe UI';c.textAlign='center';c.fillText('M',x-23,y+h/2+3);
    c.fillStyle=s.fillColor||'#ffffff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    const Z=7;for(let i=1;i<Z;i++){const bx=x+w*i/Z;c.beginPath();c.moveTo(bx,y);c.lineTo(bx,y+h);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;}
    [.3,.7].forEach(oy=>{c.beginPath();for(let p=0;p<=w;p+=2){const py=y+h*oy+Math.sin(p/w*Math.PI*8)*(h*.11);p===0?c.moveTo(x+p,py):c.lineTo(x+p,py);}c.globalAlpha=.22;c.stroke();c.globalAlpha=1;});
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='6px Segoe UI';c.textAlign='center';
    for(let i=0;i<Z;i++)c.fillText('Z'+(i+1),x+w/Z*i+w/Z/2,y+8);c.fillStyle=og;},
  sse(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(x-12,y-2,13,h+4);c.strokeRect(x-12,y-2,13,h+4);
    c.beginPath();c.arc(x-20,y+h/2,9,0,Math.PI*2);c.fill();c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 7px Segoe UI';c.textAlign='center';c.fillText('M',x-20,y+h/2+3);
    c.fillStyle=s.fillColor||'#ffffff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    const Z=5;for(let i=1;i<Z;i++){const bx=x+w*i/Z;c.beginPath();c.moveTo(bx,y);c.lineTo(bx,y+h);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;}
    c.beginPath();for(let p=0;p<=w;p+=2){const py=y+h/2+Math.sin(p/w*Math.PI*7)*(h*.3);p===0?c.moveTo(x+p,py):c.lineTo(x+p,py);}c.globalAlpha=.22;c.stroke();c.globalAlpha=1;c.fillStyle=og;},
  coknead(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    for(let i=1;i<5;i++){const bx=x+w*i/5;c.beginPath();c.moveTo(bx,y);c.lineTo(bx,y+h);c.globalAlpha=.25;c.stroke();c.globalAlpha=1;}
    const og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 7px Segoe UI';c.textAlign='center';c.fillText('KNEADER',x+w/2,y+h/2+3);c.fillStyle=og;},
  gearpump(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.27;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();c.arc(x+w/2-r*.55,y+h/2,r,0,Math.PI*2);c.stroke();
    c.beginPath();c.arc(x+w/2+r*.55,y+h/2,r,0,Math.PI*2);c.stroke();},
  screenchange(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w/2,y+h);c.globalAlpha=.4;c.stroke();c.globalAlpha=1;
    const og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';c.font='6px Segoe UI';c.textAlign='center';c.fillText('SC',x+w/2,y+h/2+3);c.fillStyle=og;},
  staticmix(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    for(let i=0;i<6;i++){const bx=x+w*i/6+w/12;c.beginPath();c.moveTo(bx,y+1);c.lineTo(bx+w/12,y+h-1);c.globalAlpha=.4;c.stroke();c.globalAlpha=1;}},
  crosshead(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y+h*.35,w,h*.3);c.strokeRect(x,y+h*.35,w,h*.3);
    c.fillRect(x+w*.35,y,w*.3,h*.38);c.strokeRect(x+w*.35,y,w*.3,h*.38);
    const oc2=c.strokeStyle;c.strokeStyle='#1a1a1a';c.lineWidth=1;
    c.beginPath();c.moveTo(x+w*.5,y+3);c.lineTo(x+w*.5,y+h*.35);c.stroke();
    c.strokeStyle=oc2;c.lineWidth=s.strokeWidth||1.5;
    const og=c.fillStyle;c.fillStyle='#1a1a1a';c.font='5.5px Segoe UI';c.textAlign='center';c.fillText('WIRE',x+w/2,y-2);c.fillStyle=og;},
  stranddie(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    for(let j=0;j<3;j++)for(let i=0;i<2;i++){c.beginPath();c.arc(x+4+i*6,y+h*.25+j*h*.22,1.8,0,Math.PI*2);c.fill();}},
  uwdie(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.38;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);c.stroke();
    for(let i=0;i<6;i++){const a=i*Math.PI/3;c.beginPath();c.moveTo(x+w/2+Math.cos(a)*r*.45,y+h/2+Math.sin(a)*r*.45);c.lineTo(x+w/2+Math.cos(a)*r,y+h/2+Math.sin(a)*r);c.globalAlpha=.45;c.stroke();c.globalAlpha=1;}},
  flatdie(c,s){const{x,y,w,h}=s;
    c.beginPath();c.moveTo(x,y);c.lineTo(x+w,y+h*.2);c.lineTo(x+w,y+h*.8);c.lineTo(x,y+h);c.closePath();c.fill();c.stroke();},
  coolingtrough(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);c.fillStyle=og;
    c.strokeStyle='rgba(0,0,0,0.3)';c.lineWidth=.8;
    for(let i=0;i<4;i++){const wx=x+w*.08+i*w*.23;c.beginPath();c.moveTo(wx,y+h*.4);c.quadraticCurveTo(wx+w*.06,y+h*.1,wx+w*.12,y+h*.4);c.stroke();}
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  cathaul(c,s){const{x,y,w,h}=s;
    [[0,0],[0,h*.6]].forEach(([,oy])=>{
      c.fillRect(x,y+oy,w,h*.35);c.strokeRect(x,y+oy,w,h*.35);
      [x+10,x+w-10].forEach(rx=>{const og=c.fillStyle;c.fillStyle='#888';c.beginPath();c.arc(rx,y+oy+h*.175,5,0,Math.PI*2);c.fill();c.stroke();c.fillStyle=og;});});},
  sparktester(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,h);
    c.setLineDash([4,2]);c.strokeStyle='rgba(0,0,0,0.5)';c.strokeRect(x,y,w,h);c.setLineDash([]);
    c.fillStyle='#333';c.font='12px Segoe UI';c.textAlign='center';c.fillText('⚡',x+w/2,y+h*.62);
    c.font='6px Segoe UI';c.fillText('HV',x+w/2,y+h*.9);c.fillStyle=og;c.strokeStyle=s.strokeColor||'#1a1a1a';},
  odgauge(c,s){const{x,y,w,h}=s;
    c.strokeStyle='rgba(0,0,0,0.5)';c.lineWidth=2;c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w/2,y+h);c.stroke();c.lineWidth=s.strokeWidth||1.5;
    c.fillStyle='#333';
    c.beginPath();c.moveTo(x,y+h*.3);c.lineTo(x+w/2-3,y+h*.5);c.lineTo(x,y+h*.7);c.closePath();c.fill();
    c.beginPath();c.moveTo(x+w,y+h*.3);c.lineTo(x+w/2+3,y+h*.5);c.lineTo(x+w,y+h*.7);c.closePath();c.fill();
    c.strokeStyle=s.strokeColor||'#1a1a1a';},
  winder(c,s){const{x,y,w,h}=s,cx=x+w/2,cy=y+h/2,r=Math.min(w,h)*.44;
    c.beginPath();c.arc(cx,cy,r,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.arc(cx,cy,r*.28,0,Math.PI*2);c.stroke();
    for(let i=0;i<4;i++){const a=i*Math.PI/2;c.beginPath();c.moveTo(cx+Math.cos(a)*r*.28,cy+Math.sin(a)*r*.28);c.lineTo(cx+Math.cos(a)*r,cy+Math.sin(a)*r);c.globalAlpha=.5;c.stroke();c.globalAlpha=1;}},
  accumulator(c,s){const{x,y,w,h}=s;
    for(let i=0;i<4;i++){const ry=y+h/(4+1)*(i+1);c.beginPath();c.arc(x+w/2,ry,w*.36,0,Math.PI*2);c.fill();c.stroke();}},
  preheater(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.strokeStyle='rgba(0,0,0,0.4)';c.lineWidth=.8;
    for(let i=1;i<5;i++){c.beginPath();c.moveTo(x+w*i/5,y);c.lineTo(x+w*i/5,y+h);c.globalAlpha=.35;c.stroke();c.globalAlpha=1;}
    c.fillStyle='#333';c.font='10px Segoe UI';c.textAlign='center';c.fillText('∿',x+w/2,y+h/2+4);
    c.fillStyle=og;c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  eccmon(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.42;
    c.strokeStyle='rgba(0,0,0,0.5)';c.lineWidth=1.5;
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);const og=c.fillStyle;c.fillStyle='#fff';c.fill();c.stroke();
    c.beginPath();c.arc(x+w/2,y+h/2,r*.38,0,Math.PI*2);c.stroke();
    c.fillStyle='#333';c.font='6px Segoe UI';c.textAlign='center';c.fillText('EC',x+w/2,y+h/2+2);
    c.fillStyle=og;c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  capstan(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.45;
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.arc(x+w/2,y+h/2,r*.25,0,Math.PI*2);c.stroke();},
  strandbath(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);c.fillStyle=og;
    c.strokeStyle='rgba(0,0,0,0.3)';c.lineWidth=.8;
    for(let i=0;i<5;i++){const wx=x+w*.06+i*w*.18;c.beginPath();c.moveTo(wx,y+h*.4);c.quadraticCurveTo(wx+w*.05,y+h*.1,wx+w*.1,y+h*.4);c.stroke();}
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  airknife(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.strokeStyle='#5b9bd5';c.lineWidth=.8;
    for(let i=0;i<5;i++){const ax=x+w*.15+i*w*.15;c.beginPath();c.moveTo(ax,y+h);c.lineTo(ax,y+h*.28);c.stroke();}
    c.fillStyle=og;c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  strandpellet(c,s){const{x,y,w,h}=s;
    [[x+w*.15,y+h*.28],[x+w*.15,y+h*.72]].forEach(([cx,cy])=>{c.beginPath();c.arc(cx,cy,h*.2,0,Math.PI*2);c.fill();c.stroke();});
    c.fillRect(x+w*.38,y+h*.1,w*.58,h*.8);c.strokeRect(x+w*.38,y+h*.1,w*.58,h*.8);
    for(let i=1;i<5;i++){c.beginPath();c.moveTo(x+w*.38+w*.58*i/5,y+h*.1);c.lineTo(x+w*.38+w*.58*i/5,y+h*.9);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;}},
  uwpellet(c,s){
    // Underwater Pelletizer — die plate top, water chamber, rotating cutter, motor bottom
    const{x,y,w,h}=s;const og=c.fillStyle;const CX=x+w/2;
    c.fillRect(x+w*.15,y,w*.7,h*.14);c.strokeRect(x+w*.15,y,w*.7,h*.14);
    c.fillStyle='rgba(0,0,0,0.18)';
    [.32,.5,.68].forEach(function(fx){c.beginPath();c.arc(x+w*fx,y+h*.07,w*.06,0,Math.PI*2);c.fill();c.stroke();});
    c.fillStyle=og;
    c.beginPath();c.moveTo(x-8,y+h*.07);c.lineTo(x+w*.15,y+h*.07);c.stroke();
    c.beginPath();c.moveTo(x+w*.1,y+h*.04);c.lineTo(x+w*.15,y+h*.07);c.lineTo(x+w*.1,y+h*.1);c.fill();
    c.fillStyle='rgba(41,128,185,0.06)';c.fillRect(x,y+h*.14,w,h*.5);c.fillStyle=og;
    c.strokeRect(x,y+h*.14,w,h*.5);
    c.beginPath();c.moveTo(x-8,y+h*.26);c.lineTo(x,y+h*.26);c.stroke();
    c.beginPath();c.moveTo(x+w,y+h*.42);c.lineTo(x+w+8,y+h*.42);c.stroke();
    c.beginPath();c.moveTo(x+w+4,y+h*.39);c.lineTo(x+w+8,y+h*.42);c.lineTo(x+w+4,y+h*.45);c.fill();
    var cR=w*.3,cCX=CX,cCY=y+h*.39;
    c.beginPath();c.arc(cCX,cCY,cR,0,Math.PI*2);
    c.fillStyle='rgba(0,0,0,0.07)';c.fill();c.stroke();c.fillStyle=og;
    for(var i=0;i<4;i++){
      var a=i*Math.PI/2;
      c.beginPath();c.moveTo(cCX,cCY);c.lineTo(cCX+Math.cos(a)*cR,cCY+Math.sin(a)*cR);
      c.globalAlpha=.45;c.stroke();c.globalAlpha=1;
    }
    c.fillRect(x+w*.15,y+h*.64,w*.7,h*.18);c.strokeRect(x+w*.15,y+h*.64,w*.7,h*.18);
    c.beginPath();c.arc(CX,y+h*.73,w*.22,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();c.fillStyle=og;
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font='bold '+Math.max(5,w*.14)+'px Segoe UI';c.textAlign='center';c.fillText('M',CX,y+h*.76);
    [[x+w*.2,x+w*.15],[x+w*.8,x+w*.85]].forEach(function(pair){c.beginPath();c.moveTo(pair[0],y+h*.82);c.lineTo(pair[1],y+h);c.stroke();});
    c.beginPath();c.moveTo(x+w*.1,y+h);c.lineTo(x+w*.9,y+h);c.stroke();
    c.fillStyle=og;},
  pelletdryer(c,s){const{x,y,w,h}=s,bH=h*.75;
    c.fillRect(x,y+h-bH,w,bH);c.strokeRect(x,y+h-bH,w,bH);
    c.beginPath();c.ellipse(x+w/2,y+h-bH,w/2,5,0,0,Math.PI*2);c.stroke();
    c.strokeStyle='rgba(0,0,0,0.4)';c.lineWidth=.8;
    for(let i=0;i<3;i++){c.beginPath();c.moveTo(x+w*.22+i*w*.28,y+h);c.lineTo(x+w*.18+i*w*.28,y+h-bH*.55);c.globalAlpha=.45;c.stroke();c.globalAlpha=1;}
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  classifier(c,s){const{x,y,w,h}=s,bH=h*.7;
    c.fillRect(x,y,w,bH);c.strokeRect(x,y,w,bH);
    c.beginPath();c.moveTo(x,y+bH);c.lineTo(x+w/2,y+h);c.lineTo(x+w,y+bH);c.stroke();
    for(let i=1;i<3;i++){c.setLineDash([3,2]);c.beginPath();c.moveTo(x,y+bH*i/3);c.lineTo(x+w,y+bH*i/3);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;c.setLineDash([]);}},
  fgsilo(c,s){const{x,y,w,h}=s,bH=h*.78;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,bH);c.strokeRect(x,y,w,bH);
    c.beginPath();c.ellipse(x+w/2,y,w/2,5,0,0,Math.PI*2);c.stroke();
    c.fillStyle='rgba(0,0,0,0.05)';c.fillRect(x+1,y+bH*.3,w-2,bH*.7);
    c.fillStyle='#fff';c.beginPath();c.moveTo(x,y+bH);c.lineTo(x+w/2,y+h);c.lineTo(x+w,y+bH);c.closePath();c.fill();c.stroke();
    c.fillStyle=og;},
  bagstation(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y+h*.2,w,h*.8);c.strokeRect(x,y+h*.2,w,h*.8);
    c.beginPath();c.moveTo(x+w*.15,y);c.lineTo(x+w*.85,y);c.lineTo(x+w,y+h*.2);c.lineTo(x,y+h*.2);c.closePath();c.fill();c.stroke();
    const og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';c.font='8px Segoe UI';c.textAlign='center';c.fillText('BAG',x+w/2,y+h*.62);c.fillStyle=og;},
  blower(c,s){const{x,y,w,h}=s,cx=x+w/2,cy=y+h/2,r=Math.min(w,h)*.44;
    c.beginPath();c.arc(cx,cy,r,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.arc(cx,cy,r,0,Math.PI*1.6);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;
    c.beginPath();c.moveTo(cx+r,cy);c.lineTo(cx+r+8,cy);c.moveTo(cx,cy-r);c.lineTo(cx,cy-r-8);c.stroke();},
  rootsblower(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.27;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();c.arc(x+w/2-r*.52,y+h/2,r,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.arc(x+w/2+r*.52,y+h/2,r,0,Math.PI*2);c.fill();c.stroke();},
  diverter2(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#f0f4fc';
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w,y+h/2);c.lineTo(x+w/2,y+h);c.lineTo(x,y+h/2);c.closePath();c.fill();c.stroke();
    c.beginPath();c.moveTo(x,y+h/2);c.lineTo(x+w*.42,y+h/2);c.moveTo(x+w*.56,y+h*.55);c.lineTo(x+w,y+h);c.moveTo(x+w*.56,y+h*.45);c.lineTo(x+w,y);c.stroke();c.fillStyle=og;},
  diverter3(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#f0f4fc';
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w,y+h/2);c.lineTo(x+w/2,y+h);c.lineTo(x,y+h/2);c.closePath();c.fill();c.stroke();
    c.beginPath();c.moveTo(x,y+h/2);c.lineTo(x+w/2,y+h/2);c.moveTo(x+w/2,y+h/2);c.lineTo(x+w,y);c.moveTo(x+w/2,y+h/2);c.lineTo(x+w,y+h);c.stroke();c.fillStyle=og;},
  filterreceiver(c,s){const{x,y,w,h}=s,bH=h*.75;
    c.fillRect(x,y,w,bH);c.strokeRect(x,y,w,bH);
    c.beginPath();c.ellipse(x+w/2,y,w/2,5,0,0,Math.PI*2);c.stroke();
    c.setLineDash([3,2]);
    for(let i=1;i<=5;i++){const fy=y+bH*.07*i;c.beginPath();c.moveTo(x+3,fy);c.lineTo(x+w-3,fy);c.globalAlpha=.35;c.stroke();c.globalAlpha=1;}
    c.setLineDash([]);
    c.beginPath();c.moveTo(x,y+bH);c.lineTo(x+w/2,y+h);c.lineTo(x+w,y+bH);c.closePath();c.fill();c.stroke();
    c.beginPath();c.moveTo(x-9,y+bH*.14);c.lineTo(x,y+bH*.14);c.stroke();},
  cyclone(c,s){const{x,y,w,h}=s,bH=h*.55;
    c.fillRect(x,y,w,bH);c.strokeRect(x,y,w,bH);
    c.fillRect(x+w,y+bH*.1,w*.44,bH*.22);c.strokeRect(x+w,y+bH*.1,w*.44,bH*.22);
    c.fillRect(x+w*.3,y-8,w*.4,bH*.28);c.strokeRect(x+w*.3,y-8,w*.4,bH*.28);
    c.beginPath();c.moveTo(x,y+bH);c.lineTo(x+w/2,y+h);c.lineTo(x+w,y+bH);c.closePath();c.fill();c.stroke();
    c.beginPath();c.arc(x+w/2,y+bH*.5,w*.22,0,Math.PI*1.5);c.globalAlpha=.35;c.stroke();c.globalAlpha=1;},
  // A long-radius elbow: straight face → bend → straight face, drawn as ONE
  // continuous sub-path. The straight faces are what a connector docks against,
  // so the joint reads as one pipe instead of a line meeting a curve; the two
  // mouths land exactly on the bbox edges, which is where ports.ts puts the
  // elbow's port anchors. Elliptical bend so it fills a non-square bbox.
  // // INVARIANT: mouth coords here must match `elbow90` in shapes/ports.ts.
  elbow90(c,s){const{x,y,w,h}=s,t=elbowStub(s),
      rx=Math.max(.5,w-2*t),ry=Math.max(.5,h-2*t),cx=x+w-t,cy=y+t,
      ax=x+t,by=y+h-t,
      cap=c.lineCap,join=c.lineJoin;
    c.lineWidth=(s.strokeWidth||1.5)*1.6;c.lineCap='round';c.lineJoin='round';
    c.beginPath();
    c.moveTo(ax,y);                                 // up-facing mouth (top edge)
    c.lineTo(ax,cy);                                // straight face
    c.ellipse(cx,cy,rx,ry,0,Math.PI,Math.PI/2,true);// the bend
    c.lineTo(x+w,by);                               // straight face → right mouth
    c.stroke();
    c.lineCap=cap;c.lineJoin=join;c.lineWidth=s.strokeWidth||1.5;},
  gatevalve(c,s){const{x,y,w,h}=s;
    c.beginPath();c.moveTo(x,y+h/2);c.lineTo(x+w,y+h/2);c.stroke();
    c.beginPath();c.moveTo(x,y);c.lineTo(x+w,y+h);c.lineTo(x+w,y);c.lineTo(x,y+h);c.closePath();c.fill();c.stroke();},
  ballvalve(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.44;
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.moveTo(x,y+h/2);c.lineTo(x+w,y+h/2);c.globalAlpha=.35;c.stroke();c.globalAlpha=1;
    c.beginPath();c.moveTo(x+w/2,y+h/2-r);c.lineTo(x+w/2,y+h/2-r*1.6);c.stroke();},
  checkvalve(c,s){const{x,y,w,h}=s;
    c.beginPath();c.moveTo(x,y+h/2);c.lineTo(x+w,y+h/2);c.stroke();
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w,y+h/2);c.lineTo(x+w/2,y+h);c.closePath();c.fill();c.stroke();
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w/2,y+h);c.stroke();},
  ctrlvalve(c,s){const{x,y,w,h}=s;
    c.beginPath();c.moveTo(x,y+h*.55);c.lineTo(x+w,y+h*.55);c.stroke();
    c.beginPath();c.moveTo(x,y+h*.38);c.lineTo(x+w,y+h*.78);c.lineTo(x+w,y+h*.38);c.lineTo(x,y+h*.78);c.closePath();c.fill();c.stroke();
    c.beginPath();c.arc(x+w/2,y+h*.16,h*.15,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.moveTo(x+w/2,y+h*.31);c.lineTo(x+w/2,y+h*.38);c.stroke();},
  pgauge(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.44;
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);c.fill();c.stroke();
    const og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 7px Segoe UI';c.textAlign='center';c.fillText('P',x+w/2,y+h/2+3);c.fillStyle=og;},
  tsensor(c,s){const{x,y,w,h}=s;
    c.lineWidth=2;c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w/2,y+h);c.stroke();c.lineWidth=s.strokeWidth||1.5;
    c.beginPath();c.arc(x+w/2,y+h/2,w*.38,0,Math.PI*2);c.fill();c.stroke();
    const og=c.fillStyle;c.fillStyle='#333';c.font='bold 7px Segoe UI';c.textAlign='center';c.fillText('T',x+w/2,y+h/2+3);c.fillStyle=og;},
  flowmeter(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w,y+h/2);c.lineTo(x+w/2,y+h);c.lineTo(x,y+h/2);c.closePath();c.fill();c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 8px Segoe UI';c.textAlign='center';c.fillText('F',x+w/2,y+h/2+3);c.fillStyle=og;},
  prv(c,s){const{x,y,w,h}=s;
    c.beginPath();c.moveTo(x,y+h*.5);c.lineTo(x+w,y+h*.5);c.stroke();
    c.beginPath();c.moveTo(x,y+h*.3);c.lineTo(x+w,y+h*.7);c.lineTo(x+w,y+h*.3);c.lineTo(x,y+h*.7);c.closePath();c.fill();c.stroke();
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w/2,y+h*.3);c.stroke();},
  centpump(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.44;
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.moveTo(x+w/2+r,y+h/2);c.lineTo(x+w/2+r+8,y+h/2);c.moveTo(x+w/2,y+h/2-r);c.lineTo(x+w/2,y+h/2-r-8);c.stroke();
    for(let i=0;i<4;i++){const a=i*Math.PI/2;c.beginPath();c.moveTo(x+w/2+Math.cos(a)*r*.25,y+h/2+Math.sin(a)*r*.25);c.lineTo(x+w/2+Math.cos(a+.3)*r*.85,y+h/2+Math.sin(a+.3)*r*.85);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;}},
  chiller(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.fillStyle='#333';c.font='16px Segoe UI';c.textAlign='center';c.fillText('❄',x+w/2,y+h*.58);
    c.fillStyle='#555';c.font='6.5px Segoe UI';c.fillText('CHILLER',x+w/2,y+h*.88);c.fillStyle=og;},
  compressor(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.34;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);c.stroke();
    c.beginPath();c.moveTo(x,y+h/2);c.lineTo(x-8,y+h/2);c.moveTo(x+w,y+h/2);c.lineTo(x+w+8,y+h/2);c.stroke();},
  motor(c,s){const{x,y,w,h}=s,r=Math.min(w,h)*.44;
    c.beginPath();c.arc(x+w/2,y+h/2,r,0,Math.PI*2);c.fill();c.stroke();
    const og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';c.font=`bold ${Math.min(13,w*.4)}px Segoe UI`;c.textAlign='center';c.fillText('M',x+w/2,y+h/2+4);c.fillStyle=og;},
  controlpanel(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;c.fillStyle='#fff';c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    const cols=['#107c10','#c42b1c','#d79b00','#0078d4'];
    for(let i=0;i<3;i++)for(let j=0;j<4;j++){c.fillStyle=cols[(i*4+j)%4];c.beginPath();c.arc(x+8+j*(w-16)/3.5,y+10+i*(h-30)/2.5,3,0,Math.PI*2);c.fill();}
    c.fillStyle='#ddd';c.fillRect(x+4,y+h*.7,w-8,h*.22);c.strokeStyle='#bbb';c.strokeRect(x+4,y+h*.7,w-8,h*.22);
    c.fillStyle=og;c.strokeStyle=s.strokeColor||'#1a1a1a';},
  metaldet(c,s){const{x,y,w,h}=s;
    c.lineWidth=2;c.beginPath();c.moveTo(x,y+h*.5);c.lineTo(x+w,y+h*.5);c.stroke();c.lineWidth=s.strokeWidth||1.5;
    c.fillRect(x,y,w*.38,h);c.strokeRect(x,y,w*.38,h);
    c.fillRect(x+w*.62,y,w*.38,h);c.strokeRect(x+w*.62,y,w*.38,h);},
  heatex(c,s){const{x,y,w,h}=s;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    for(let i=1;i<=4;i++){c.beginPath();c.moveTo(x+w*.1,y+h*i/5);c.lineTo(x+w*.9,y+h*i/5);c.globalAlpha=.2;c.stroke();c.globalAlpha=1;}
    c.strokeStyle='rgba(0,0,0,0.4)';c.lineWidth=.8;
    c.beginPath();c.moveTo(x,y+h*.3);c.lineTo(x+w*.5,y+h*.3);c.quadraticCurveTo(x+w*.65,y+h*.3,x+w*.65,y+h*.5);c.lineTo(x+w*.65,y+h*.7);c.stroke();
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  textlabel(c,s){
    const og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`${s.fontSize||12}px Segoe UI`;c.textAlign='left';
    c.fillText(s.label||'Text Label',s.x+4,s.y+s.h/2+4);c.fillStyle=og;},
  zonebox(c,s){const{x,y,w,h}=s;
    const og=c.fillStyle;
    c.fillStyle=s.fillColor||'rgba(43,87,154,0.04)';c.fillRect(x,y,w,h);
    c.setLineDash([8,4]);c.strokeStyle=s.strokeColor||'rgba(43,87,154,0.5)';c.lineWidth=1.5;c.strokeRect(x,y,w,h);c.setLineDash([]);
    // Multi-line body text: paragraphs (split on newline) + word-wrap, clipped to the box.
    const pad=8, fs=s.fontSize||12, lh=fs*1.35;
    const txt=(s.label&&s.label!=='Zone Box')?s.label:'';
    c.save();
    c.beginPath();c.rect(x,y,w,h);c.clip();
    c.textAlign='left';c.textBaseline='alphabetic';c.font=`${fs}px Segoe UI`;
    if(!txt){
      c.fillStyle='rgba(43,87,154,0.4)';c.fillText('Double-click to write…',x+pad,y+pad+fs);
    }else{
      c.fillStyle=s.strokeColor||'rgba(43,87,154,0.95)';
      const maxw=w-pad*2;let cy=y+pad+fs;
      for(let para of txt.split('\n')){
        if(cy>y+h-2)break;
        if(/^\s*[-*]\s+/.test(para))para=para.replace(/^(\s*)[-*]\s+/,'$1• '); // "- " / "* " → bullet
        if(para===''){cy+=lh;continue;}
        const words=para.split(' ');let line='';
        for(let i=0;i<words.length;i++){
          const test=line?line+' '+words[i]:words[i];
          if(c.measureText(test).width>maxw&&line){
            c.fillText(line,x+pad,cy);cy+=lh;if(cy>y+h-2){line='';break;}line=words[i];
          }else line=test;
        }
        if(line&&cy<=y+h-2){c.fillText(line,x+pad,cy);cy+=lh;}
      }
    }
    c.restore();
    c.fillStyle=og;c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;},
  processarrow(c,s){const{x,y,w,h}=s,tip=w*.28;
    const og=c.fillStyle;c.fillStyle=s.fillColor||'rgba(43,87,154,0.6)';
    c.beginPath();c.moveTo(x,y+h*.25);c.lineTo(x+w-tip,y+h*.25);c.lineTo(x+w-tip,y);c.lineTo(x+w,y+h/2);c.lineTo(x+w-tip,y+h);c.lineTo(x+w-tip,y+h*.75);c.lineTo(x,y+h*.75);c.closePath();c.fill();c.stroke();c.fillStyle=og;},

  // ══════ FEEDERS ══════

  scsfeeder(c,s){
    // Single screw feeder: hopper top + single screw barrel + motor
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // barrel (main body)
    c.fillRect(x,y+h*.35,w,h*.35);c.strokeRect(x,y+h*.35,w,h*.35);
    // hopper on top-left
    c.beginPath();c.moveTo(x+w*.05,y);c.lineTo(x+w*.45,y);c.lineTo(x+w*.38,y+h*.35);c.lineTo(x+w*.12,y+h*.35);c.closePath();c.fill();c.stroke();
    // single helix line
    c.beginPath();
    for(let i=0;i<=w;i+=2){const hy=y+h*.525+Math.sin(i/w*Math.PI*6)*(h*.12);i===0?c.moveTo(x+i,hy):c.lineTo(x+i,hy);}
    c.globalAlpha=.35;c.stroke();c.globalAlpha=1;
    // motor circle right
    c.beginPath();c.arc(x+w+10,y+h*.525,8,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();c.fillStyle=og;
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 6px Segoe UI';c.textAlign='center';c.fillText('M',x+w+10,y+h*.525+2);
    // discharge arrow right
    c.beginPath();c.moveTo(x+w,y+h*.525);c.lineTo(x+w+2,y+h*.525);c.stroke();
    c.fillStyle=og;},

  tcsfeeder(c,s){
    // Twin screw feeder: hopper + barrel with TWO interleaved screws
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    c.fillRect(x,y+h*.35,w,h*.38);c.strokeRect(x,y+h*.35,w,h*.38);
    c.beginPath();c.moveTo(x+w*.05,y);c.lineTo(x+w*.45,y);c.lineTo(x+w*.38,y+h*.35);c.lineTo(x+w*.12,y+h*.35);c.closePath();c.fill();c.stroke();
    // two helical screws offset vertically
    [.46,.63].forEach((oy,idx)=>{
      c.beginPath();
      for(let i=0;i<=w;i+=2){const hy=y+h*oy+Math.sin((i/w*Math.PI*6)+(idx*Math.PI))*(h*.09);i===0?c.moveTo(x+i,hy):c.lineTo(x+i,hy);}
      c.globalAlpha=.35;c.stroke();c.globalAlpha=1;
    });
    // motor
    c.beginPath();c.arc(x+w+10,y+h*.54,8,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();c.fillStyle=og;
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 6px Segoe UI';c.textAlign='center';c.fillText('M',x+w+10,y+h*.54+2);
    c.fillStyle=og;},

  liquidfeeder(c,s){
    // Liquid feeder: elevated tank + pump unit + metering tube
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // Storage tank (upper vessel, horizontal ellipsoid)
    const tH=h*.32,tY=y,r=w*.18;
    c.fillRect(x+r,tY,w-r*2,tH);c.strokeRect(x+r,tY,w-r*2,tH);
    c.beginPath();c.ellipse(x+r,tY+tH/2,r*.35,tH/2,0,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.ellipse(x+w-r,tY+tH/2,r*.35,tH/2,0,0,Math.PI*2);c.fill();c.stroke();
    // Connecting pipe down
    c.beginPath();c.moveTo(x+w/2,tY+tH);c.lineTo(x+w/2,y+h*.55);c.stroke();
    // Pump body (centrifugal pump symbol)
    c.beginPath();c.arc(x+w*.35,y+h*.65,w*.18,0,Math.PI*2);c.fill();c.stroke();
    // Metering unit (tall thin cylinder)
    c.fillRect(x+w*.62,y+h*.45,w*.28,h*.45);c.strokeRect(x+w*.62,y+h*.45,w*.28,h*.45);
    // Piston rod
    c.lineWidth=(s.strokeWidth||1.5)*1.5;
    c.beginPath();c.moveTo(x+w*.76,y+h*.38);c.lineTo(x+w*.76,y+h*.45);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;
    // Outlet arrow
    c.beginPath();c.moveTo(x+w*.9,y+h*.68);c.lineTo(x+w+6,y+h*.68);c.stroke();
    // PUMP label
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 5px Segoe UI';c.textAlign='center';
    c.fillText('PUMP',x+w*.35,y+h*.66);c.fillStyle=og;},

  manualdebag(c,s){
    // Manual Debagging Station: operator + tipping table + receiving hopper
    const{x,y,w,h}=s;
    const og=c.fillStyle;

    // ─── OPERATOR (left side) ─────────────────────────────────────────────
    // Proportions: head ~10% h, torso ~22% h, legs ~28% h
    const opX=x+w*.14;   // operator center X

    // Hard hat (filled circle)
    const headR=h*.055, headY=y+h*.10;
    c.beginPath();c.arc(opX,headY,headR,0,Math.PI*2);c.fill();c.stroke();

    // Hard hat brim (slightly wider flat arc)
    c.lineWidth=(s.strokeWidth||1.5)*1.2;
    c.beginPath();c.moveTo(opX-headR*1.5,headY);c.lineTo(opX+headR*1.5,headY);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;

    // Torso (rectangle)
    const torsoY=headY+headR, torsoH=h*.20, torsoW=w*.10;
    c.fillRect(opX-torsoW/2, torsoY, torsoW, torsoH);
    c.strokeRect(opX-torsoW/2, torsoY, torsoW, torsoH);

    // Arms: one reaching forward toward table
    c.beginPath();
    c.moveTo(opX+torsoW/2, torsoY+torsoH*.25);
    c.lineTo(x+w*.30, torsoY+torsoH*.55);
    c.stroke();
    // Other arm hanging down
    c.beginPath();
    c.moveTo(opX-torsoW/2, torsoY+torsoH*.2);
    c.lineTo(opX-torsoW/2-w*.03, torsoY+torsoH*.8);
    c.stroke();

    // Legs
    const legTopY=torsoY+torsoH, legBotY=y+h*.82;
    // Left leg (slightly bent = working stance)
    c.beginPath();c.moveTo(opX-torsoW*.15,legTopY);
    c.lineTo(opX-torsoW*.4,legTopY+(legBotY-legTopY)*.55);
    c.lineTo(opX-torsoW*.5,legBotY);c.stroke();
    // Right leg
    c.beginPath();c.moveTo(opX+torsoW*.15,legTopY);
    c.lineTo(opX+torsoW*.15,legBotY);c.stroke();
    // Feet
    c.beginPath();c.moveTo(opX-torsoW*.5,legBotY);c.lineTo(opX-torsoW*.1,legBotY);c.stroke();
    c.beginPath();c.moveTo(opX+torsoW*.05,legBotY);c.lineTo(opX+torsoW*.38,legBotY);c.stroke();

    // ─── TIPPING / DUMP TABLE (center) ────────────────────────────────────
    // Angled platform (bag sits on this, tilted ~20° toward hopper)
    const tblX=x+w*.28, tblW=w*.22, tblY=y+h*.28, tblH=h*.06;
    c.save();
    c.translate(tblX+tblW/2, tblY+tblH/2);
    c.rotate(-0.32);  // ~18° tilt
    c.fillRect(-tblW/2,-tblH/2,tblW,tblH);
    c.strokeRect(-tblW/2,-tblH/2,tblW,tblH);
    c.restore();

    // Table legs (2 vertical + cross brace)
    const legX1=tblX+tblW*.15, legX2=tblX+tblW*.85;
    [[legX1],[legX2]].forEach(([lx])=>{
      c.beginPath();c.moveTo(lx,tblY+tblH*.5);c.lineTo(lx,y+h*.85);c.stroke();
    });
    c.beginPath();c.moveTo(legX1,y+h*.65);c.lineTo(legX2,y+h*.65);c.stroke();
    // Ground line
    c.beginPath();c.moveTo(legX1-w*.04,y+h*.85);c.lineTo(legX2+w*.04,y+h*.85);c.stroke();

    // Bag on table (simplified rectangle tilted with table)
    c.save();c.translate(tblX+tblW*.45,tblY-tblH*.3);c.rotate(-0.28);
    c.fillStyle='rgba(0,0,0,0.06)';
    c.fillRect(-tblW*.4,-tblH*2,tblW*.8,tblH*2.2);
    c.fillStyle=og;c.strokeRect(-tblW*.4,-tblH*2,tblW*.8,tblH*2.2);
    c.restore();

    // ─── RECEIVING HOPPER (right side) ─────────────────────────────────────
    const hpX=x+w*.52, hpW=w*.44;
    const hpTop=y+h*.10, hpMid=y+h*.58, nkW=hpW*.28, hpBot=y+h*.80;

    // Wide opening top → narrow neck (trapezoidal funnel)
    c.beginPath();
    c.moveTo(hpX,hpTop);
    c.lineTo(hpX+hpW,hpTop);
    c.lineTo(hpX+hpW/2+nkW/2,hpMid);
    c.lineTo(hpX+hpW/2-nkW/2,hpMid);
    c.closePath();c.fill();c.stroke();

    // Neck / cylinder body below funnel
    c.fillRect(hpX+hpW/2-nkW/2,hpMid,nkW,hpBot-hpMid);
    c.strokeRect(hpX+hpW/2-nkW/2,hpMid,nkW,hpBot-hpMid);

    // Discharge outlet at bottom
    const outW=nkW*.55, outY=hpBot, outH=h*.08;
    c.fillRect(hpX+hpW/2-outW/2,outY,outW,outH);
    c.strokeRect(hpX+hpW/2-outW/2,outY,outW,outH);

    // Hopper stand legs (2 legs)
    const hl1=hpX+hpW*.12, hl2=hpX+hpW*.88;
    [[hl1],[hl2]].forEach(([lx])=>{
      c.beginPath();c.moveTo(lx,hpTop+h*.02);c.lineTo(lx,y+h*.88);c.stroke();
    });

    // Dust hood top (slight arc over hopper opening)
    c.setLineDash([3,2]);c.globalAlpha=.35;
    c.beginPath();c.moveTo(hpX-w*.02,hpTop);c.lineTo(hpX+hpW+w*.02,hpTop);c.stroke();
    c.setLineDash([]);c.globalAlpha=1;

    c.fillStyle=og;},

  divertervalve(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;const CX=x+w/2;
    var actH=h*.28,actW=w*.55;
    c.fillRect(CX-actW/2,y,actW,actH);c.strokeRect(CX-actW/2,y,actW,actH);
    c.beginPath();c.moveTo(CX,y+actH);c.lineTo(CX,y+actH+h*.08);c.stroke();
    c.fillRect(CX-actW*.7,y+actH+h*.08,actW*1.4,h*.1);c.strokeRect(CX-actW*.7,y+actH+h*.08,actW*1.4,h*.1);
    var vbY=y+actH+h*.18,vbR=w*.28;
    c.beginPath();c.arc(CX,vbY+vbR,vbR,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.arc(CX,vbY+vbR,vbR*.45,0,Math.PI*2);
    c.fillStyle='rgba(0,0,0,0.1)';c.fill();c.stroke();c.fillStyle=og;
    c.beginPath();c.moveTo(CX,vbY);c.lineTo(CX,y+actH+h*.18);c.stroke();
    var legY=vbY+vbR*2,legLen=h*.18;
    c.beginPath();c.moveTo(CX,legY);c.lineTo(x+w*.15,legY+legLen);c.stroke();
    c.beginPath();c.moveTo(x+w*.15,legY+legLen);c.lineTo(x+w*.15,y+h);c.stroke();
    c.beginPath();c.moveTo(CX,legY);c.lineTo(CX+w*.18,legY+legLen);c.stroke();
    c.beginPath();c.moveTo(CX+w*.18,legY+legLen);c.lineTo(CX+w*.18,y+h);c.stroke();
    c.fillStyle=og;},

  connectingtube(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;const CX=x+w/2,tubeW=w*.55;
    c.fillRect(CX-w*.45,y,w*.9,h*.08);c.strokeRect(CX-w*.45,y,w*.9,h*.08);
    c.fillRect(CX-tubeW/2,y+h*.08,tubeW,h*.84);c.strokeRect(CX-tubeW/2,y+h*.08,tubeW,h*.84);
    c.globalAlpha=.12;
    [-.15,0,.15].forEach(function(ox){c.beginPath();c.moveTo(CX+w*ox,y+h*.12);c.lineTo(CX+w*ox,y+h*.88);c.stroke();});
    c.globalAlpha=1;
    c.fillRect(CX-w*.45,y+h*.92,w*.9,h*.08);c.strokeRect(CX-w*.45,y+h*.92,w*.9,h*.08);
    c.fillStyle='rgba(0,0,0,0.2)';
    [-.33,-.15,.15,.33].forEach(function(ox){
      c.beginPath();c.arc(CX+w*ox,y+h*.04,1.8,0,Math.PI*2);c.fill();
      c.beginPath();c.arc(CX+w*ox,y+h*.96,1.8,0,Math.PI*2);c.fill();
    });
    c.fillStyle=og;},

  degassing(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;
    c.beginPath();c.moveTo(x-8,y+h*.65);c.lineTo(x,y+h*.65);c.stroke();
    c.beginPath();c.moveTo(x-4,y+h*.62);c.lineTo(x,y+h*.65);c.lineTo(x-4,y+h*.68);c.fill();
    c.fillRect(x,y+h*.55,w*.35,h*.45);c.strokeRect(x,y+h*.55,w*.35,h*.45);
    var chR=Math.min(w*.32,h*.32),chCX=x+w*.65,chCY=y+h*.52;
    c.beginPath();c.arc(chCX,chCY,chR,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.arc(chCX,chCY,chR*.5,0,Math.PI*2);c.globalAlpha=.2;c.stroke();c.globalAlpha=1;
    [chR*.35,chR*-.05].forEach(function(ox){
      c.beginPath();c.moveTo(chCX-ox,chCY-chR);c.lineTo(chCX-ox,chCY-chR-h*.15);c.stroke();
      c.beginPath();c.moveTo(chCX-ox-3,chCY-chR-h*.12);c.lineTo(chCX-ox,chCY-chR-h*.17);c.lineTo(chCX-ox+3,chCY-chR-h*.12);c.fill();
    });
    c.beginPath();c.moveTo(x+w*.35,y+h*.65);c.lineTo(chCX-chR,y+h*.65);c.stroke();
    c.beginPath();c.moveTo(chCX+chR,chCY);c.lineTo(x+w+6,chCY);c.stroke();
    c.beginPath();c.moveTo(x+w+2,chCY-3);c.lineTo(x+w+6,chCY);c.lineTo(x+w+2,chCY+3);c.fill();
    c.fillStyle=og;},

  pprollers(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;
    var nR=5,rR=h*.3,ry=y+h*.5,sp=w/(nR+1);
    c.lineWidth=(s.strokeWidth||1.5)*1.2;
    c.beginPath();c.moveTo(x,y+h*.18);c.lineTo(x+w,y+h*.18);c.stroke();
    c.beginPath();c.moveTo(x,y+h*.82);c.lineTo(x+w,y+h*.82);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;
    for(var i=1;i<=nR;i++){
      var rx=x+sp*i;
      c.fillStyle=s.fillColor||'#ffffff';
      c.beginPath();c.ellipse(rx,ry,rR*.45,rR,0,0,Math.PI*2);c.fill();c.stroke();
    }
    c.setLineDash([4,3]);c.globalAlpha=.4;
    c.beginPath();c.moveTo(x,y+h*.18+2);c.lineTo(x+w,y+h*.18+2);c.stroke();
    c.setLineDash([]);c.globalAlpha=1;c.fillStyle=og;},

  stretchoven(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.fillStyle='rgba(255,200,100,0.08)';
    c.fillRect(x+w*.04,y+h*.12,w*.92,h*.76);c.fillStyle=og;
    c.strokeRect(x+w*.04,y+h*.12,w*.92,h*.76);
    c.strokeStyle='rgba(200,80,0,0.35)';c.lineWidth=1.2;
    [.32,.5,.68].forEach(function(fy){
      var ey=y+h*fy;c.beginPath();c.moveTo(x+w*.07,ey);
      for(var i=0;i<7;i++){c.lineTo(x+w*(.07+i*.126),ey+(i%2===0?-h*.06:h*.06));}
      c.lineTo(x+w*.94,ey);c.stroke();
    });
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;
    c.setLineDash([5,3]);c.globalAlpha=.4;
    c.beginPath();c.moveTo(x-8,y+h*.5);c.lineTo(x+w+8,y+h*.5);c.stroke();
    c.setLineDash([]);c.globalAlpha=1;
    var fs=Math.max(7,h*.16);
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold '+fs+'px Segoe UI';
    c.textAlign='center';c.fillText('OVEN',x+w/2,y+h*.54+fs*.3);c.fillStyle=og;},

  quenchbath(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;var wL=y+h*.28;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.fillStyle='rgba(41,128,185,0.1)';c.fillRect(x+w*.02,wL,w*.96,h-(wL-y));c.fillStyle=og;
    c.beginPath();c.moveTo(x+w*.02,wL);c.lineTo(x+w*.98,wL);
    c.strokeStyle='rgba(41,128,185,0.4)';c.stroke();c.strokeStyle=s.strokeColor||'#1a1a1a';
    c.strokeStyle='rgba(41,128,185,0.45)';c.lineWidth=1.5;
    c.beginPath();c.moveTo(x+w*.05,y+h*.68);
    for(var i=0;i<7;i++){
      c.quadraticCurveTo(x+w*(.05+i*.13+.065),y+h*(i%2===0?.56:.76),x+w*(.05+(i+1)*.13),y+h*.68);
    }
    c.stroke();c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;
    c.setLineDash([4,3]);c.globalAlpha=.45;
    c.beginPath();c.moveTo(x-8,y+h*.18);
    c.quadraticCurveTo(x+w*.25,y+h*.18,x+w*.25,wL+8);
    c.quadraticCurveTo(x+w*.75,wL+8,x+w*.75,y+h*.18);
    c.lineTo(x+w+8,y+h*.18);c.stroke();
    c.setLineDash([]);c.globalAlpha=1;
    c.beginPath();c.moveTo(x+w*.2,y+h);c.lineTo(x+w*.2,y+h+6);c.stroke();
    c.beginPath();c.moveTo(x+w*.8,y+h);c.lineTo(x+w*.8,y+h+6);c.stroke();
    c.fillStyle=og;},

  takeuprollers(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;var rR=h*.3;
    c.lineWidth=(s.strokeWidth||1.5)*1.1;
    c.beginPath();c.moveTo(x,y+2);c.lineTo(x+w,y+2);c.stroke();
    c.beginPath();c.moveTo(x,y+h-2);c.lineTo(x+w,y+h-2);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;
    var nR=3,sp=w/(nR+1);
    for(var i=1;i<=nR;i++){
      var rx=x+sp*i,ry=y+h/2;
      c.fillStyle=s.fillColor||'#ffffff';
      c.beginPath();c.ellipse(rx,ry,rR*.5,rR,0,0,Math.PI*2);c.fill();c.stroke();
      c.globalAlpha=.2;
      c.beginPath();c.moveTo(rx-rR*.35,ry-rR*.6);c.lineTo(rx+rR*.35,ry+rR*.6);c.stroke();
      c.beginPath();c.moveTo(rx+rR*.35,ry-rR*.6);c.lineTo(rx-rR*.35,ry+rR*.6);c.stroke();
      c.globalAlpha=1;
    }
    c.beginPath();c.moveTo(x-8,y+h*.3);c.lineTo(x+sp*.8,y+h*.3);c.stroke();
    c.beginPath();c.moveTo(x+sp*2.2,y+h*.3);c.lineTo(x+w+8,y+h*.3);c.stroke();
    c.fillStyle=og;},

  takeupwinder(c,s){
    const{x,y,w,h}=s;const og=c.fillStyle;
    var nSt=5,stW=w/nSt;
    c.lineWidth=(s.strokeWidth||1.5)*1.4;
    c.beginPath();c.moveTo(x,y+h*.08);c.lineTo(x+w,y+h*.08);c.stroke();
    c.beginPath();c.moveTo(x,y+h*.92);c.lineTo(x+w,y+h*.92);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;
    for(var i=1;i<nSt;i++){
      c.beginPath();c.moveTo(x+stW*i,y+h*.08);c.lineTo(x+stW*i,y+h*.92);
      c.globalAlpha=.2;c.stroke();c.globalAlpha=1;
    }
    for(var j=0;j<nSt;j++){
      var sx=x+stW*j,scx=sx+stW/2;
      c.fillRect(sx+stW*.1,y+h*.1,stW*.8,h*.18);c.strokeRect(sx+stW*.1,y+h*.1,stW*.8,h*.18);
      c.fillStyle='rgba(0,0,0,0.08)';c.fillRect(sx+stW*.2,y+h*.13,stW*.6,h*.1);c.fillStyle=s.fillColor||'#ffffff';
      var wR=Math.min(stW*.24,h*.2),wCY=y+h*.62;
      c.beginPath();c.arc(scx,wCY,wR,0,Math.PI*2);c.fill();c.stroke();
      c.beginPath();c.arc(scx,wCY,wR*1.35,0,Math.PI*2);c.globalAlpha=.2;c.stroke();c.globalAlpha=1;
      c.beginPath();c.arc(scx,wCY,wR*1.65,0,Math.PI*2);c.globalAlpha=.12;c.stroke();c.globalAlpha=1;
      c.beginPath();c.moveTo(scx-wR*.6,wCY);c.lineTo(scx+wR*.6,wCY);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;
      c.beginPath();c.moveTo(scx,wCY+wR*1.7);c.lineTo(scx,y+h*.9);c.stroke();
      c.fillRect(scx-stW*.25,y+h*.88,stW*.5,h*.04);c.strokeRect(scx-stW*.25,y+h*.88,stW*.5,h*.04);
    }
    c.fillStyle=og;},

    hoistdebag(c,s){
    // Hoist Debagging Station
    // Structure: overhead frame → chain hoist + hook → FIBC bulk bag hanging
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;

    // ── Overhead hoist frame (horizontal top bar + vertical posts) ──
    // Top horizontal beam
    c.lineWidth=(s.strokeWidth||1.5)*1.4;
    c.beginPath();c.moveTo(x+w*.05,y+h*.08);c.lineTo(x+w*.95,y+h*.08);c.stroke();
    // Left post
    c.beginPath();c.moveTo(x+w*.12,y+h*.08);c.lineTo(x+w*.12,y+h*.15);c.stroke();
    // Right post
    c.beginPath();c.moveTo(x+w*.88,y+h*.08);c.lineTo(x+w*.88,y+h*.15);c.stroke();
    // Cross braces on frame top
    c.beginPath();c.moveTo(x+w*.25,y+h*.02);c.lineTo(x+w*.5,y+h*.08);c.stroke();
    c.beginPath();c.moveTo(x+w*.75,y+h*.02);c.lineTo(x+w*.5,y+h*.08);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;

    // ── Chain hoist body (small rectangle on top beam, center) ──
    const hoistW=w*.28,hoistH=h*.08;
    c.fillRect(CX-hoistW/2,y,hoistW,hoistH);
    c.strokeRect(CX-hoistW/2,y,hoistW,hoistH);
    // Hoist hook chain (vertical line from hoist body down to bag)
    c.beginPath();c.moveTo(CX,y+hoistH);c.lineTo(CX,y+h*.22);c.stroke();
    // Hook (small J shape)
    c.beginPath();c.arc(CX,y+h*.23,h*.02,0,Math.PI);c.stroke();

    // ── FIBC Bulk Bag (hanging) ──
    // Bag body: trapezoid — wide at top (loop loops), narrows slightly at bottom
    const bagTop=y+h*.25,bagBot=y+h*.82;
    const bagTW=w*.72,bagBW=w*.52;
    const btl=CX-bagTW/2,btr=CX+bagTW/2;
    const bbl=CX-bagBW/2,bbr=CX+bagBW/2;

    // Bag fill (light grey interior)
    c.fillStyle='rgba(0,0,0,0.04)';
    c.beginPath();c.moveTo(btl,bagTop);c.lineTo(btr,bagTop);
    c.lineTo(bbr,bagBot);c.lineTo(bbl,bagBot);c.closePath();c.fill();
    c.fillStyle=s.fillColor||'#ffffff';

    // Bag outline
    c.beginPath();c.moveTo(btl,bagTop);c.lineTo(btr,bagTop);
    c.lineTo(bbr,bagBot);c.lineTo(bbl,bagBot);c.closePath();c.stroke();

    // Top loop straps (4 straps going up to hook)
    const loopOff=w*.1;
    [[btl+loopOff,bagTop],[btr-loopOff,bagTop]].forEach(([lx,ly])=>{
      c.beginPath();c.moveTo(lx,ly);c.lineTo(CX,y+h*.24);c.stroke();
    });

    // Bag horizontal seam lines (2 stitching lines)
    [.45,.65].forEach(fy=>{
      const by=bagTop+(bagBot-bagTop)*fy;
      const wx_=bagTW/2-(bagTW/2-bagBW/2)*fy;
      c.setLineDash([4,3]);c.beginPath();
      c.moveTo(CX-wx_,by);c.lineTo(CX+wx_,by);
      c.globalAlpha=.3;c.stroke();c.globalAlpha=1;c.setLineDash([]);
    });

    // ── Discharge spout at bottom of bag ──
    const spoutW=w*.16,spoutH=h*.08;
    c.fillRect(CX-spoutW/2,bagBot,spoutW,spoutH);
    c.strokeRect(CX-spoutW/2,bagBot,spoutW,spoutH);
    // Spout neck taper
    c.beginPath();c.moveTo(bbl+w*.05,bagBot);c.lineTo(CX-spoutW/2,bagBot);c.stroke();
    c.beginPath();c.moveTo(bbr-w*.05,bagBot);c.lineTo(CX+spoutW/2,bagBot);c.stroke();

    // Label "FIBC"
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.max(6,w*.11)}px Segoe UI`;
    c.textAlign='center';
    c.fillText('FIBC',CX,(bagTop+bagBot)/2+3);
    c.fillStyle=og;},

  debaghopper(c,s){
    // Debagging Hopper — rectangular upper tank + steep cone + discharge gate + stand
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;

    // ── Upper rectangular tank body ──
    const tankH=h*.42,tankY=y+h*.05;
    c.fillRect(x+w*.05,tankY,w*.9,tankH);
    c.strokeRect(x+w*.05,tankY,w*.9,tankH);

    // ── Top cover/lid (slight elevation, flat with small vent) ──
    c.fillRect(x+w*.05,y,w*.9,h*.06);
    c.strokeRect(x+w*.05,y,w*.9,h*.06);
    // Vent / inlet port on top center
    c.fillRect(CX-w*.08,y-h*.04,w*.16,h*.05);
    c.strokeRect(CX-w*.08,y-h*.04,w*.16,h*.05);

    // ── Steep conical hopper bottom ──
    const coneTop=tankY+tankH, coneBot=y+h*.82;
    const coneNeckW=w*.18;
    c.beginPath();
    c.moveTo(x+w*.05,coneTop);
    c.lineTo(x+w*.95,coneTop);
    c.lineTo(CX+coneNeckW/2,coneBot);
    c.lineTo(CX-coneNeckW/2,coneBot);
    c.closePath();c.fill();c.stroke();

    // Cone angle reinforcing lines
    c.globalAlpha=.2;
    c.beginPath();c.moveTo(x+w*.28,coneTop);c.lineTo(CX,coneBot);c.stroke();
    c.beginPath();c.moveTo(x+w*.72,coneTop);c.lineTo(CX,coneBot);c.stroke();
    c.globalAlpha=1;

    // ── Discharge gate/valve at bottom of cone ──
    const gateY=coneBot;
    c.fillRect(CX-w*.12,gateY,w*.24,h*.06);
    c.strokeRect(CX-w*.12,gateY,w*.24,h*.06);
    // Gate actuator (small square on side)
    c.fillRect(CX+w*.12,gateY+h*.01,w*.1,h*.04);
    c.strokeRect(CX+w*.12,gateY+h*.01,w*.1,h*.04);

    // ── Support stand (4 legs) ──
    const legTop=y+h*.82,legBot=y+h;
    [[x+w*.15,x+w*.1],[x+w*.85,x+w*.9]].forEach(([lx1,lx2])=>{
      c.beginPath();c.moveTo(lx1,legTop);c.lineTo(lx2,legBot);c.stroke();
    });
    // Leg cross brace
    c.beginPath();c.moveTo(x+w*.1,legBot);c.lineTo(x+w*.9,legBot);c.stroke();

    // ── Control box on right side ──
    c.fillRect(x+w*.88,tankY+tankH*.3,w*.14,h*.14);
    c.strokeRect(x+w*.88,tankY+tankH*.3,w*.14,h*.14);
    // Small indicator lights
    c.fillStyle='rgba(0,0,0,0.3)';
    c.fillRect(x+w*.90,tankY+tankH*.34,w*.04,h*.03);
    c.fillRect(x+w*.90,tankY+tankH*.42,w*.04,h*.03);
    c.fillStyle=og;

    // ── Level indicator line (dashed, shows material level ~60%) ──
    const lvl=tankY+tankH*.4;
    c.setLineDash([3,2]);c.globalAlpha=.3;
    c.beginPath();c.moveTo(x+w*.06,lvl);c.lineTo(x+w*.94,lvl);c.stroke();
    c.setLineDash([]);c.globalAlpha=1;
    c.fillStyle=og;},

  rawmatsilo(c,s){
    // Raw Material Feeder Silo
    // Structure: tall cylinder body + conical discharge + rotary valve base +
    //            side-mounted motor/gearbox + dust filter on top
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;

    // ── Dust filter / vent on top ──
    const filterW=w*.32,filterH=h*.06;
    c.fillRect(CX-filterW/2,y,filterW,filterH);
    c.strokeRect(CX-filterW/2,y,filterW,filterH);
    // Filter pleats
    for(let i=1;i<4;i++){
      c.beginPath();c.moveTo(CX-filterW/2+filterW*i/4,y);
      c.lineTo(CX-filterW/2+filterW*i/4,y+filterH);
      c.globalAlpha=.3;c.stroke();c.globalAlpha=1;
    }

    // ── Cylindrical body ──
    const cylTop=y+filterH,cylH=h*.48;
    // Top ellipse cap
    c.beginPath();c.ellipse(CX,cylTop,w*.42,h*.03,0,0,Math.PI*2);
    c.fill();c.stroke();
    // Cylinder walls
    c.fillRect(x+w*.08,cylTop,w*.84,cylH);
    c.strokeRect(x+w*.08,cylTop,w*.84,cylH);
    // Vertical weld seam lines on cylinder
    c.globalAlpha=.12;
    c.beginPath();c.moveTo(CX,cylTop);c.lineTo(CX,cylTop+cylH);c.stroke();
    c.globalAlpha=1;

    // Material level fill inside cylinder (75% full)
    c.fillStyle='rgba(0,0,0,0.05)';
    c.fillRect(x+w*.09,cylTop+cylH*.05,w*.82,cylH*.70);
    c.fillStyle=s.fillColor||'#ffffff';

    // ── Steep conical discharge bottom ──
    const coneTop=cylTop+cylH, coneH=h*.22;
    const coneBot=coneTop+coneH, neckW=w*.22;
    c.beginPath();
    c.moveTo(x+w*.08,coneTop);
    c.lineTo(x+w*.92,coneTop);
    c.lineTo(CX+neckW/2,coneBot);
    c.lineTo(CX-neckW/2,coneBot);
    c.closePath();c.fill();c.stroke();

    // ── Rotary valve / discharge unit at cone base ──
    const rvY=coneBot,rvH=h*.10,rvW=w*.38;
    c.fillRect(CX-rvW/2,rvY,rvW,rvH);
    c.strokeRect(CX-rvW/2,rvY,rvW,rvH);
    // Rotor blades inside valve (4 spokes)
    const rvc={x:CX,y:rvY+rvH/2},rvr=rvH*.42;
    for(let i=0;i<4;i++){
      const a=i*Math.PI/2;
      c.beginPath();c.moveTo(rvc.x,rvc.y);
      c.lineTo(rvc.x+Math.cos(a)*rvr,rvc.y+Math.sin(a)*rvr);
      c.globalAlpha=.5;c.stroke();c.globalAlpha=1;
    }

    // ── Motor + gearbox on right side of rotary valve ──
    // Gearbox (rectangle)
    const gbX=CX+rvW/2,gbY=rvY,gbW=w*.2,gbH=rvH;
    c.fillRect(gbX,gbY,gbW,gbH);c.strokeRect(gbX,gbY,gbW,gbH);
    // Motor (circle after gearbox)
    c.beginPath();c.arc(gbX+gbW+w*.12,gbY+gbH/2,w*.12,0,Math.PI*2);
    c.fill();c.stroke();
    // Motor M label
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.max(5,w*.1)}px Segoe UI`;c.textAlign='center';
    c.fillText('M',gbX+gbW+w*.12,gbY+gbH/2+3);
    c.fillStyle=og;
    // Connecting shaft line between motor and gearbox
    c.beginPath();c.moveTo(gbX+gbW,gbY+gbH/2);c.lineTo(gbX+gbW+w*.02,gbY+gbH/2);c.stroke();

    // ── Outlet at very bottom ──
    const outY=rvY+rvH,outH=h*.04;
    c.beginPath();c.moveTo(CX-neckW*.4,outY);c.lineTo(CX-neckW*.4,outY+outH);c.stroke();
    c.beginPath();c.moveTo(CX+neckW*.4,outY);c.lineTo(CX+neckW*.4,outY+outH);c.stroke();

    c.fillStyle=og;},

  debagging(c,s){
    // Debagging hopper: large opening hopper with access hatch + bag rest platform
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // Platform top (bag rest, wide rectangle)
    c.fillStyle='rgba(150,170,200,0.18)';c.fillRect(x,y,w,h*.22);c.fillStyle=og;
    c.strokeRect(x,y,w,h*.22);
    // Platform grating lines
    for(let i=1;i<5;i++){c.beginPath();c.moveTo(x+w*i/5,y);c.lineTo(x+w*i/5,y+h*.22);c.globalAlpha=.2;c.stroke();c.globalAlpha=1;}
    // Main hopper (trapezoidal)
    const nw=w*.25,off=(w-nw)/2;
    c.beginPath();c.moveTo(x,y+h*.22);c.lineTo(x+w,y+h*.22);c.lineTo(x+off+nw,y+h*.72);c.lineTo(x+off,y+h*.72);c.closePath();c.fill();c.stroke();
    // Discharge chute (rectangle bottom)
    c.fillRect(x+off,y+h*.72,nw,h*.28);c.strokeRect(x+off,y+h*.72,nw,h*.28);
    // Dust hood lines on top (open top)
    c.beginPath();c.moveTo(x+w*.15,y);c.lineTo(x+w*.15,y-8);c.moveTo(x+w*.85,y);c.lineTo(x+w*.85,y-8);c.stroke();
    // Label
    c.fillStyle=og;},

  forcedfeeder(c,s){
    // Forced Feeder — VERTICAL layout
    // Top-to-bottom: Gearbox + Motor (top) → Twin-screw vertical barrel → Conical discharge → Stub outlet
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;

    // ── Gearbox housing (top, 18% h, full width) ──
    const gbH=h*.18, gbW=w*.70, gbX=CX-gbW/2, gbY=y;
    c.fillRect(gbX,gbY,gbW,gbH);
    c.strokeRect(gbX,gbY,gbW,gbH);
    // Two meshing gear circles inside gearbox
    const gcr=gbH*.28;
    const gcx1=gbX+gbW*.35, gcx2=gbX+gbW*.65, gcy=gbY+gbH*.5;
    c.beginPath();c.arc(gcx1,gcy,gcr,0,Math.PI*2);c.stroke();
    c.beginPath();c.arc(gcx2,gcy,gcr,0,Math.PI*2);c.stroke();
    // Gear teeth hint (small radial lines on each circle)
    [gcx1,gcx2].forEach(gx=>{
      for(let i=0;i<8;i++){
        const a=i*Math.PI/4;
        c.beginPath();
        c.moveTo(gx+Math.cos(a)*gcr,gcy+Math.sin(a)*gcr);
        c.lineTo(gx+Math.cos(a)*(gcr+2.5),gcy+Math.sin(a)*(gcr+2.5));
        c.globalAlpha=.5;c.stroke();c.globalAlpha=1;
      }
    });

    // ── Motor circle (sits on top of gearbox, centre) ──
    const mR=h*.09, mCX=CX, mCY=gbY-mR-1;
    c.beginPath();c.arc(mCX,mCY,mR,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.max(5,mR*0.85)}px Segoe UI`;c.textAlign='center';
    c.fillText('M',mCX,mCY+mR*0.38);
    c.fillStyle=og;
    // Drive shaft line: motor bottom → gearbox top
    c.beginPath();c.moveTo(CX,mCY+mR);c.lineTo(CX,gbY);c.stroke();

    // ── Vertical barrel / casing (40% h, centred) ──
    const barY=gbY+gbH, barH=h*.40, barW=w*.62, barX=CX-barW/2;
    c.fillRect(barX,barY,barW,barH);
    c.strokeRect(barX,barY,barW,barH);

    // ── Twin vertical screws inside barrel (two interleaved cosine waves along Y axis) ──
    // Each screw drawn as a sine wave along the vertical axis
    const lw=c.lineWidth;
    c.lineWidth=(s.strokeWidth||1.5)*0.85;
    [-.25,.25].forEach((xOff,idx)=>{
      c.beginPath();
      for(let i=0;i<=barH;i+=2){
        const px=CX+xOff*barW+Math.sin((i/barH)*Math.PI*6+(idx*Math.PI))*(barW*0.18);
        i===0?c.moveTo(px,barY+i):c.lineTo(px,barY+i);
      }
      c.globalAlpha=.45;c.stroke();c.globalAlpha=1;
    });
    c.lineWidth=lw;

    // ── Feed inlet opening on LEFT side of barrel (mid-height) ──
    const inletH=barH*.22, inletW=w*.20, inletY=barY+barH*.38;
    c.fillRect(barX-inletW,inletY,inletW,inletH);
    c.strokeRect(barX-inletW,inletY,inletW,inletH);
    // Arrow pointing right into barrel
    c.beginPath();c.moveTo(barX-inletW-6,inletY+inletH/2);c.lineTo(barX-2,inletY+inletH/2);c.stroke();
    c.beginPath();
    c.moveTo(barX-5,inletY+inletH/2-4);
    c.lineTo(barX-1,inletY+inletH/2);
    c.lineTo(barX-5,inletY+inletH/2+4);
    c.fill();

    // ── Conical discharge at bottom (20% h) ──
    const conY=barY+barH, conH=h*.20, nkW=w*.22;
    c.beginPath();
    c.moveTo(barX,conY);
    c.lineTo(barX+barW,conY);
    c.lineTo(CX+nkW/2,conY+conH);
    c.lineTo(CX-nkW/2,conY+conH);
    c.closePath();c.fill();c.stroke();

    // ── Short discharge stub at very bottom ──
    const stubH=h*.08;
    c.fillRect(CX-nkW/2,conY+conH,nkW,stubH);
    c.strokeRect(CX-nkW/2,conY+conH,nkW,stubH);

    c.fillStyle=og;},

  granulessilo(c,s){
    // Granules Silo: tall flat-bottomed cylindrical silo +
    // rectangular filter/vent box on top + 4 hanging load-cell legs +
    // rotary valve / motor unit at base.
    // Matches reference image closely.
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;

    // ── Filter / dust vent box on top (15% h, 55% w) ──
    const fW=w*.55, fH=h*.12, fX=CX-fW/2, fY=y;
    c.fillRect(fX,fY,fW,fH);
    c.strokeRect(fX,fY,fW,fH);
    // Filter pleats (vertical lines inside box)
    const nPleats=5;
    for(let i=1;i<nPleats;i++){
      c.beginPath();c.moveTo(fX+fW*i/nPleats,fY);c.lineTo(fX+fW*i/nPleats,fY+fH);
      c.globalAlpha=.3;c.stroke();c.globalAlpha=1;
    }

    // ── Cylindrical silo body (flat top + straight walls, 52% h) ──
    const cylY=fY+fH, cylH=h*.50, cylX=x+w*.05, cylW=w*.90;
    // Flat top ellipse cap
    c.beginPath();c.ellipse(CX,cylY,cylW/2,h*.025,0,0,Math.PI*2);
    c.fill();c.stroke();
    // Cylinder walls
    c.fillRect(cylX,cylY,cylW,cylH);
    c.strokeRect(cylX,cylY,cylW,cylH);
    // Subtle material fill (top 20% empty, rest filled)
    c.fillStyle='rgba(0,0,0,0.04)';
    c.fillRect(cylX+1,cylY+cylH*.18,cylW-2,cylH*.80);
    c.fillStyle=og;

    // ── 4 load-cell hanging legs (short vertical brackets from bottom of cylinder) ──
    const legY=cylY+cylH, legH=h*.10;
    const legXs=[cylX+cylW*.10, cylX+cylW*.30, cylX+cylW*.70, cylX+cylW*.90];
    legXs.forEach(lx=>{
      c.beginPath();c.moveTo(lx,legY);c.lineTo(lx,legY+legH);c.stroke();
      // Small load cell diamond
      c.beginPath();c.moveTo(lx,legY+legH);c.lineTo(lx-4,legY+legH+5);c.lineTo(lx,legY+legH+10);c.lineTo(lx+4,legY+legH+5);c.closePath();
      c.fillStyle='rgba(0,0,0,0.2)';c.fill();c.stroke();c.fillStyle=og;
    });

    // ── Flat conical transition (slight taper, 8% h) ──
    const conY=legY+legH+h*.04, conH=h*.06;
    const nkW=w*.30;
    c.beginPath();
    c.moveTo(cylX,conY);
    c.lineTo(cylX+cylW,conY);
    c.lineTo(CX+nkW/2,conY+conH);
    c.lineTo(CX-nkW/2,conY+conH);
    c.closePath();c.fill();c.stroke();

    // ── Rotary valve / discharge unit (wide rectangle) ──
    const rvY=conY+conH, rvH=h*.10, rvW=nkW*1.2;
    c.fillRect(CX-rvW/2,rvY,rvW,rvH);
    c.strokeRect(CX-rvW/2,rvY,rvW,rvH);
    // Rotor inside (circle with 4 spokes)
    const rcx=CX, rcy=rvY+rvH/2, rvr=rvH*.38;
    c.beginPath();c.arc(rcx,rcy,rvr,0,Math.PI*2);c.stroke();
    for(let i=0;i<4;i++){const a=i*Math.PI/2;
      c.beginPath();c.moveTo(rcx,rcy);c.lineTo(rcx+Math.cos(a)*rvr,rcy+Math.sin(a)*rvr);
      c.globalAlpha=.5;c.stroke();c.globalAlpha=1;}

    // ── Motor circle (to the right of rotary valve) ──
    const mR2=rvH*.42, mCX2=CX+rvW/2+mR2+2, mCY2=rvY+rvH/2;
    c.beginPath();c.arc(mCX2,mCY2,mR2,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.max(5,mR2*0.85)}px Segoe UI`;c.textAlign='center';
    c.fillText('M',mCX2,mCY2+mR2*0.35);
    c.fillStyle=og;},

  powdersilo(c,s){
    // Powder Silo: tall silo on 4-leg stand + steep cone discharge +
    // dome filter on top + small pressure relief nozzle + rotary valve + motor at base.
    // Matches reference image closely.
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;

    // ── Dome / pressure-relief nozzle on very top ──
    // Small cylinder cap
    const capW=w*.20, capH=h*.04, capX=CX-capW/2, capY=y;
    c.fillRect(capX,capY,capW,capH);
    c.strokeRect(capX,capY,capW,capH);
    // Dome (half-ellipse on top of cap)
    c.beginPath();c.ellipse(CX,capY,capW/2,capH*0.9,0,Math.PI,0);
    c.fill();c.stroke();

    // ── Dust filter box (rectangular, beside nozzle, 12% w × 8% h) ──
    const flW=w*.22, flH=h*.07, flX=CX+capW/2+2, flY=capY+capH*0.1;
    c.fillRect(flX,flY,flW,flH);
    c.strokeRect(flX,flY,flW,flH);
    // Filter pleats
    for(let i=1;i<4;i++){
      c.beginPath();c.moveTo(flX+flW*i/4,flY);c.lineTo(flX+flW*i/4,flY+flH);
      c.globalAlpha=.3;c.stroke();c.globalAlpha=1;
    }

    // ── Cylindrical silo body (38% h) ──
    const cylY=capY+capH+h*.02, cylH=h*.36, cylX=x+w*.08, cylW=w*.84;
    c.beginPath();c.ellipse(CX,cylY,cylW/2,h*.022,0,0,Math.PI*2);
    c.fill();c.stroke();
    c.fillRect(cylX,cylY,cylW,cylH);
    c.strokeRect(cylX,cylY,cylW,cylH);
    // Material level fill
    c.fillStyle='rgba(0,0,0,0.04)';
    c.fillRect(cylX+1,cylY+cylH*.15,cylW-2,cylH*.83);
    c.fillStyle=og;

    // ── Steep conical hopper (26% h) ──
    const conY=cylY+cylH, conH=h*.24, nkW=w*.18;
    c.beginPath();
    c.moveTo(cylX,conY);
    c.lineTo(cylX+cylW,conY);
    c.lineTo(CX+nkW/2,conY+conH);
    c.lineTo(CX-nkW/2,conY+conH);
    c.closePath();c.fill();c.stroke();

    // ── 4-leg support stand (lines from cone base to ground) ──
    const standY=conY+conH, groundY=y+h, legW=w*.08;
    [[cylX+legW, cylX+legW*0.2],
     [cylX+cylW-legW, cylX+cylW-legW*0.2]].forEach(([topX,botX])=>{
      c.beginPath();c.moveTo(topX,conY+conH*0.5);c.lineTo(botX,groundY);c.stroke();
    });
    // Horizontal cross brace
    c.beginPath();c.moveTo(cylX+legW*0.5,groundY-h*.04);c.lineTo(cylX+cylW-legW*0.5,groundY-h*.04);c.stroke();

    // ── Rotary valve / discharge (wide rect below cone) ──
    const rvY=standY, rvH=h*.09, rvW=nkW*1.25;
    c.fillRect(CX-rvW/2,rvY,rvW,rvH);
    c.strokeRect(CX-rvW/2,rvY,rvW,rvH);
    // Rotor circle + 4 spokes
    const rcx=CX, rcy=rvY+rvH/2, rvr=rvH*.36;
    c.beginPath();c.arc(rcx,rcy,rvr,0,Math.PI*2);c.stroke();
    for(let i=0;i<4;i++){const a=i*Math.PI/2;
      c.beginPath();c.moveTo(rcx,rcy);c.lineTo(rcx+Math.cos(a)*rvr,rcy+Math.sin(a)*rvr);
      c.globalAlpha=.5;c.stroke();c.globalAlpha=1;}

    // ── Motor (circle to right of rotary valve) ──
    const mR=rvH*.40, mCX=CX+rvW/2+mR+2, mCY=rvY+rvH/2;
    c.beginPath();c.arc(mCX,mCY,mR,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.max(5,mR*0.85)}px Segoe UI`;c.textAlign='center';
    c.fillText('M',mCX,mCY+mR*0.35);
    c.fillStyle=og;},

  pelletscanner(c,s){
    // Pellet scanner / optical sorter: rectangular housing + lens + conveyor belt
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // Main housing (tall box)
    c.fillStyle='#fff';c.fillRect(x,y,w,h*.65);c.fillStyle=og;
    c.strokeRect(x,y,w,h*.65);
    // Scan window (dark rectangle in middle)
    c.fillStyle='rgba(20,30,80,0.25)';c.fillRect(x+w*.15,y+h*.12,w*.7,h*.28);c.fillStyle=og;
    c.strokeRect(x+w*.15,y+h*.12,w*.7,h*.28);
    // Scan beam lines (from window)
    c.strokeStyle='rgba(0,0,0,0.35)';c.lineWidth=.8;
    for(let i=0;i<5;i++){
      c.beginPath();c.moveTo(x+w*.15+w*.7*i/4,y+h*.4);c.lineTo(x+w*.25+w*.5*i/4,y+h*.65);c.stroke();
    }
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;
    // Conveyor belt (bottom)
    c.fillStyle='#f0f0f0';c.fillRect(x,y+h*.65,w,h*.2);c.fillStyle=og;
    c.strokeRect(x,y+h*.65,w,h*.2);
    [x+8,x+w-8].forEach(bx=>{c.beginPath();c.arc(bx,y+h*.75,7,0,Math.PI*2);c.fillStyle='#aaa';c.fill();c.stroke();c.fillStyle=og;});
    // Reject outlet (bottom-right arrow)
    c.beginPath();c.moveTo(x+w,y+h*.72);c.lineTo(x+w+8,y+h*.72);c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 6px Segoe UI';c.textAlign='center';
    c.fillText('SCAN',x+w/2,y+h*.09);c.fillStyle=og;},

  // ══════ COMPOUNDING DOWNSTREAM ══════

  hotcutpellet(c,s){
    // Dry/Hot cut — teardrop/pin shape with rotating cutter
    const{x,y,w,h}=s;const og=c.fillStyle;
    const CX=x+w/2,pinR=Math.min(w,h*.62)*.48,pinCY=y+pinR+2;
    c.beginPath();
    c.arc(CX,pinCY,pinR,Math.PI*.3,Math.PI*.7,true);
    c.lineTo(CX,y+h*.88);c.closePath();
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();
    c.beginPath();c.arc(CX,pinCY,pinR*.28,0,Math.PI*2);
    c.fillStyle='rgba(0,0,0,0.18)';c.fill();c.stroke();c.fillStyle=og;
    c.lineWidth=(s.strokeWidth||1.5)*1.5;
    c.beginPath();c.moveTo(CX-pinR*.55,pinCY-pinR*.2);c.lineTo(CX+pinR*.55,pinCY+pinR*.2);c.stroke();
    c.beginPath();c.moveTo(CX+pinR*.15,pinCY-pinR*.62);c.lineTo(CX-pinR*.15,pinCY+pinR*.62);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(x,pinCY-pinR*.28,w*.18,pinR*.56);c.strokeRect(x,pinCY-pinR*.28,w*.18,pinR*.56);
    c.fillRect(CX-w*.08,y+h*.88,w*.16,h*.12);c.strokeRect(CX-w*.08,y+h*.88,w*.16,h*.12);
    c.fillStyle=og;},
  waterringpellet(c,s){
    // Water ring pelletizer: die plate + water ring housing + centrifugal dryer outlet
    const{x,y,w,h}=s;
    const og=c.fillStyle;

    // ── Die head (left block) ──
    c.fillRect(x,y+h*.15,w*.25,h*.7);c.strokeRect(x,y+h*.15,w*.25,h*.7);
    // Die holes
    for(let i=0;i<4;i++){
      c.beginPath();c.arc(x+w*.125,y+h*.3+i*h*.14,2.5,0,Math.PI*2);
      c.fillStyle='rgba(0,0,0,0.2)';c.fill();c.stroke();c.fillStyle=og;
    }

    // ── Water ring housing (main large circle) ──
    const wr=h*.35,cx2=x+w*.53,cy2=y+h*.5;
    c.beginPath();c.arc(cx2,cy2,wr,0,Math.PI*2);c.fill();c.stroke();
    // Water ring (annular ring inside)
    c.beginPath();c.arc(cx2,cy2,wr*.75,0,Math.PI*2);
    c.fillStyle='rgba(0,0,0,0.06)';c.fill();
    c.strokeStyle='rgba(0,0,0,0.35)';c.lineWidth=1.5;c.stroke();
    c.fillStyle=og;c.strokeStyle=s.strokeColor||'#1a1a1a';

    // Water nozzle arrows (tangential, showing rotation)
    c.strokeStyle='rgba(0,0,0,0.3)';c.lineWidth=.8;
    for(let i=0;i<4;i++){
      const a=i*Math.PI/2;const r=wr*.75;
      c.beginPath();
      c.moveTo(cx2+Math.cos(a)*r,cy2+Math.sin(a)*r);
      c.lineTo(cx2+Math.cos(a+.5)*(r-4),cy2+Math.sin(a+.5)*(r-4));
      c.stroke();
    }
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;

    // Inner cutter blades (4 lines)
    c.lineWidth=(s.strokeWidth||1.5)*1.3;
    for(let i=0;i<4;i++){
      const a=i*Math.PI/2;
      c.beginPath();c.moveTo(cx2,cy2);c.lineTo(cx2+Math.cos(a)*wr*.55,cy2+Math.sin(a)*wr*.55);c.stroke();
    }
    c.lineWidth=s.strokeWidth||1.5;
    // Center hub
    c.beginPath();c.arc(cx2,cy2,wr*.14,0,Math.PI*2);
    c.fillStyle='rgba(0,0,0,0.15)';c.fill();c.stroke();c.fillStyle=og;

    // ── Slurry outlet pipe (right side, going to dryer) ──
    c.beginPath();c.moveTo(cx2+wr,cy2);c.lineTo(x+w,cy2);c.stroke();
    c.beginPath();c.moveTo(x+w-6,cy2-5);c.lineTo(x+w,cy2);c.lineTo(x+w-6,cy2+5);c.fill();

    // ── Water inlet (top pipe) ──
    c.beginPath();c.moveTo(cx2,cy2-wr);c.lineTo(cx2,y);c.stroke();

    // ── Connecting pipe from die to housing ──
    c.beginPath();c.moveTo(x+w*.25,y+h*.5);c.lineTo(cx2-wr,y+h*.5);c.stroke();

    // Label
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 5.5px Segoe UI';c.textAlign='center';
    c.fillText('WATER RING',cx2,y+h*.08);c.fillStyle=og;},

  centdewater(c,s){
    // Centrifugal dryer — vertical column, helical screw vanes, upward airflow arrows
    const{x,y,w,h}=s;const og=c.fillStyle;const CX=x+w/2;
    c.fillRect(x,y+h*.06,w,h*.82);c.strokeRect(x,y+h*.06,w,h*.82);
    c.fillRect(x+w*.2,y,w*.6,h*.07);c.strokeRect(x+w*.2,y,w*.6,h*.07);
    c.lineWidth=(s.strokeWidth||1.5)*1.3;
    c.beginPath();c.moveTo(CX,y-6);c.lineTo(CX,y+1);c.stroke();
    c.beginPath();c.moveTo(CX-4,y-2);c.lineTo(CX,y+2);c.lineTo(CX+4,y-2);c.fill();
    c.lineWidth=s.strokeWidth||1.5;
    var vTop=y+h*.1,vBot=y+h*.84,vH=(vBot-vTop)/5;
    for(var i=0;i<5;i++){
      var vy=vTop+i*vH;
      c.beginPath();
      if(i%2===0){c.moveTo(x+w*.12,vy);c.lineTo(x+w*.88,vy+vH*.85);}
      else{c.moveTo(x+w*.88,vy);c.lineTo(x+w*.12,vy+vH*.85);}
      c.globalAlpha=.5;c.stroke();c.globalAlpha=1;
    }
    c.fillStyle=s.strokeColor||'#1a1a1a';
    [.28,.5,.72].forEach(function(fx){
      var ax=x+w*fx,ay=y+h*.6;
      c.beginPath();c.moveTo(ax,ay);c.lineTo(ax,ay-h*.2);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;
      c.beginPath();c.moveTo(ax-3,ay-h*.18);c.lineTo(ax,ay-h*.23);c.lineTo(ax+3,ay-h*.18);c.fill();
    });
    c.beginPath();c.moveTo(x,y+h*.55);c.lineTo(x-8,y+h*.55);c.stroke();
    c.fillRect(CX-w*.18,y+h*.88,w*.36,h*.12);c.strokeRect(CX-w*.18,y+h*.88,w*.36,h*.12);
    c.fillStyle=og;},
  aircoolingvib(c,s){
    // Air cooling vibrator: long inclined trough on vibrating springs + air nozzles below
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // Inclined trough (slightly angled rectangle)
    c.save();c.translate(x+w/2,y+h/2);c.rotate(-0.06);
    c.fillRect(-w/2,-h*.3,w,h*.35);c.strokeRect(-w/2,-h*.3,w,h*.35);
    // Pellet material inside (light blue fill)
    c.fillStyle='rgba(0,0,0,0.05)';c.fillRect(-w/2+2,-h*.3+2,w-4,h*.35-4);
    c.fillStyle=og;
    // Perforation holes (air nozzle dots along bottom of trough)
    c.fillStyle=s.strokeColor||'#1a1a1a';
    for(let i=0;i<7;i++){c.beginPath();c.arc(-w/2+w*i/6+w/12,-h*.3+h*.28,2,0,Math.PI*2);c.fill();}
    c.restore();
    // Vibration springs (4 spring symbols below trough)
    const sy=y+h*.45;
    [.15,.38,.62,.85].forEach(fx=>{
      const bx=x+w*fx;
      for(let k=0;k<4;k++){
        c.beginPath();c.moveTo(bx+(k%2===0?-3:3),sy+k*4);c.lineTo(bx+(k%2===0?3:-3),sy+k*4+4);c.globalAlpha=.6;c.stroke();c.globalAlpha=1;
      }
      // Base plate
      c.beginPath();c.moveTo(bx-6,sy+18);c.lineTo(bx+6,sy+18);c.stroke();
    });
    // Air inlet pipes (arrows from bottom)
    [.25,.75].forEach(fx=>{
      c.beginPath();c.moveTo(x+w*fx,y+h);c.lineTo(x+w*fx,y+h*.75);c.stroke();
    });
    // Feed inlet arrow (left)
    c.beginPath();c.moveTo(x-8,y+h*.18);c.lineTo(x,y+h*.18);c.stroke();
    // Discharge arrow (right)
    c.beginPath();c.moveTo(x+w,y+h*.25);c.lineTo(x+w+8,y+h*.25);c.stroke();
    // Label
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 6px Segoe UI';c.textAlign='center';
    c.fillText('AIR COOL',x+w/2,y+h*.74);c.fillStyle=og;},

  fluidizedbed(c,s){
    // Fluidized bed — tall rectangular vessel, inlet arrow top, inner filter box,
    // cone discharge, rotary valve base, side outlet
    const{x,y,w,h}=s;const og=c.fillStyle;const CX=x+w/2;
    c.beginPath();c.moveTo(CX,y-8);c.lineTo(CX,y+1);c.stroke();
    c.beginPath();c.moveTo(CX-4,y-4);c.lineTo(CX,y+1);c.lineTo(CX+4,y-4);c.fill();
    c.fillRect(x+w*.15,y,w*.7,h*.06);c.strokeRect(x+w*.15,y,w*.7,h*.06);
    var bodyTop=y+h*.06,bodyH=h*.68;
    c.fillRect(x,bodyTop,w,bodyH);c.strokeRect(x,bodyTop,w,bodyH);
    c.fillStyle='rgba(255,255,255,0.6)';
    c.fillRect(x+w*.1,bodyTop+h*.05,w*.8,bodyH*.55);
    c.strokeRect(x+w*.1,bodyTop+h*.05,w*.8,bodyH*.55);
    c.fillStyle=og;
    c.fillRect(x+w,bodyTop+bodyH*.35,w*.25,h*.07);c.strokeRect(x+w,bodyTop+bodyH*.35,w*.25,h*.07);
    var coneTop=bodyTop+bodyH,coneH=h*.14,nkW=w*.3;
    c.beginPath();c.moveTo(x,coneTop);c.lineTo(x+w,coneTop);
    c.lineTo(CX+nkW/2,coneTop+coneH);c.lineTo(CX-nkW/2,coneTop+coneH);
    c.closePath();c.fill();c.stroke();
    var rvY=coneTop+coneH,rvH=h*.12;
    c.fillRect(CX-nkW/2,rvY,nkW,rvH);c.strokeRect(CX-nkW/2,rvY,nkW,rvH);
    c.beginPath();c.moveTo(CX-nkW*.35,rvY+rvH/2);c.lineTo(CX+nkW*.35,rvY+rvH/2);c.globalAlpha=.4;c.stroke();c.globalAlpha=1;
    c.beginPath();c.moveTo(CX,rvY+2);c.lineTo(CX,rvY+rvH-2);c.globalAlpha=.4;c.stroke();c.globalAlpha=1;
    c.beginPath();c.moveTo(CX,rvY+rvH);c.lineTo(CX,rvY+rvH+6);c.stroke();
    c.fillStyle=og;},
  vacloader(c,s){
    // Vacuum loader: inverted cone on legs + filter body + vacuum connection on top
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // Filter body (cylinder, upper 45%)
    const fbH=h*.42,fbY=y;
    c.fillRect(x+w*.1,fbY,w*.8,fbH);c.strokeRect(x+w*.1,fbY,w*.8,fbH);
    c.beginPath();c.ellipse(x+w/2,fbY,w*.4,h*.04,0,0,Math.PI*2);c.fill();c.stroke();
    // Vacuum line connection at top
    c.beginPath();c.moveTo(x+w/2,fbY);c.lineTo(x+w/2,fbY-10);c.stroke();
    c.beginPath();c.moveTo(x+w/2-8,fbY-10);c.lineTo(x+w/2+8,fbY-10);c.stroke();
    // Suction inlet pipe (right side)
    c.beginPath();c.moveTo(x+w*.9,fbY+fbH*.35);c.lineTo(x+w+8,fbY+fbH*.35);c.stroke();
    // Material hopper (inverted cone, lower 35%)
    const coneY=fbY+fbH;
    c.beginPath();c.moveTo(x+w*.1,coneY);c.lineTo(x+w*.9,coneY);c.lineTo(x+w*.5,coneY+h*.35);c.closePath();c.fill();c.stroke();
    // Discharge gate (flap at bottom of cone)
    c.fillStyle='#888';c.fillRect(x+w*.42,coneY+h*.35-3,w*.16,5);c.strokeRect(x+w*.42,coneY+h*.35-3,w*.16,5);c.fillStyle=og;
    // Support legs
    [.28,.72].forEach(fx=>{
      c.beginPath();c.moveTo(x+w*fx,coneY+h*.35);c.lineTo(x+w*(fx-.08),y+h);c.stroke();
      c.beginPath();c.moveTo(x+w*fx,coneY+h*.35);c.lineTo(x+w*(fx+.08),y+h);c.stroke();
    });
    // VAC label
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 6.5px Segoe UI';c.textAlign='center';
    c.fillText('VAC',x+w/2,fbY+fbH*.55);c.fillStyle=og;},

  vacreceiver(c,s){
    // Vacuum receiver: horizontal vessel on stand + vacuum nozzle top + material outlet bottom
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const vR=h*.28;
    // Horizontal vessel (cylinder)
    c.fillRect(x+vR,y,w-vR*2,h*.58);c.strokeRect(x+vR,y,w-vR*2,h*.58);
    c.beginPath();c.ellipse(x+vR,y+h*.29,vR*.35,h*.29,0,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.ellipse(x+w-vR,y+h*.29,vR*.35,h*.29,0,0,Math.PI*2);c.fill();c.stroke();
    // Filter bag inside (dashed vertical rectangle)
    c.setLineDash([3,2]);
    c.strokeRect(x+w*.35,y+h*.06,w*.3,h*.44);
    c.setLineDash([]);
    // Vacuum connection on top
    c.beginPath();c.moveTo(x+w*.5,y);c.lineTo(x+w*.5,y-10);c.stroke();
    c.beginPath();c.moveTo(x+w*.35,y-10);c.lineTo(x+w*.65,y-10);c.stroke();
    // Material inlet (left arrow)
    c.beginPath();c.moveTo(x-8,y+h*.25);c.lineTo(x+vR,y+h*.25);c.stroke();
    // Material outlet (cone bottom)
    c.beginPath();c.moveTo(x+w*.3,y+h*.58);c.lineTo(x+w/2,y+h*.82);c.lineTo(x+w*.7,y+h*.58);c.closePath();c.fill();c.stroke();
    c.beginPath();c.moveTo(x+w/2,y+h*.82);c.lineTo(x+w/2,y+h);c.stroke();
    // Stand legs
    [.3,.7].forEach(fx=>{c.beginPath();c.moveTo(x+w*fx,y+h*.82);c.lineTo(x+w*fx,y+h);c.stroke();});
    c.beginPath();c.moveTo(x+w*.25,y+h);c.lineTo(x+w*.75,y+h);c.stroke();
    // VAC RCV label
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold 6px Segoe UI';c.textAlign='center';
    c.fillText('VAC RCV',x+w/2,y+h*.32);c.fillStyle=og;},

  titleblock(c,s){const{x,y,w,h}=s;
    const docMeta=S.docMeta;
    const docN=docMeta&&docMeta.docName?docMeta.docName:'New PFD Diagram';
    c.fillStyle='#f8f9fa';c.fillRect(x,y,w,h);
    c.fillStyle='#2b579a';c.fillRect(x,y,w,h*.32);
    c.fillStyle='#fff';c.font='bold 9px Segoe UI';c.textAlign='left';c.fillText('JEDDAH CABLES COMPANY',x+6,y+h*.2);
    c.fillStyle='#1a1a1a';c.font='bold 8px Segoe UI';c.textAlign='left';
    c.fillText(docN,x+6,y+h*.55);
    c.fillStyle='#666';c.font='7px Segoe UI';
    c.fillText('Rev: '+(docMeta.rev||'A')+'   |   '+(docMeta.date||''),x+6,y+h*.8);
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.strokeRect(x,y,w,h);},

  // ══════ NEW SYMBOLS v3 ══════

  supermixer(c,s){
    // Super Mixer (Hot) — square vessel, S-paddle agitator, motor+gearbox top, control panel side
    const{x,y,w,h}=s;const og=c.fillStyle;
    const CX=x+w*.52,CY=y+h*.48;
    var vx=x+w*.04,vw=w*.6,vy=y+h*.05,vh=h*.78;
    c.fillRect(vx,vy,vw,vh);c.strokeRect(vx,vy,vw,vh);
    c.fillRect(vx-2,vy-h*.04,vw+4,h*.05);c.strokeRect(vx-2,vy-h*.04,vw+4,h*.05);
    c.beginPath();c.moveTo(vx,vy+vh);c.lineTo(vx+vw*.45,vy+vh);
    c.lineTo(vx+vw*.35,vy+vh+h*.08);c.lineTo(vx,vy+vh+h*.08);c.closePath();c.fill();c.stroke();
    c.beginPath();c.moveTo(CX,vy+h*.04);c.lineTo(CX,vy+vh-h*.04);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;
    c.lineWidth=(s.strokeWidth||1.5)*2;
    c.beginPath();c.moveTo(vx+vw*.1,CY-h*.04);c.lineTo(vx+vw*.88,CY-h*.04);c.stroke();
    c.beginPath();c.moveTo(vx+vw*.1,CY+h*.04);c.lineTo(vx+vw*.88,CY+h*.04);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;
    [vx+vw*.1,vx+vw*.88].forEach(function(bx){c.beginPath();c.moveTo(bx,CY-h*.08);c.lineTo(bx,CY+h*.08);c.stroke();});
    var gbx=vx+vw*.6,gby=vy-h*.04;
    c.fillRect(gbx,gby-h*.08,vw*.35,h*.08);c.strokeRect(gbx,gby-h*.08,vw*.35,h*.08);
    c.beginPath();c.arc(gbx+vw*.35+h*.07,gby-h*.04,h*.06,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();c.fillStyle=og;
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='bold '+Math.max(5,h*.07)+'px Segoe UI';
    c.textAlign='center';c.fillText('M',gbx+vw*.35+h*.07,gby-h*.015);
    var cpx=vx+vw+2,cpy=vy+vh*.3,cpw=w*.2,cph=vh*.35;
    c.fillStyle=s.fillColor||'#ffffff';c.fillRect(cpx,cpy,cpw,cph);c.strokeRect(cpx,cpy,cpw,cph);
    c.fillStyle='rgba(0,0,0,0.1)';c.fillRect(cpx+2,cpy+cph*.1,cpw-4,cph*.45);
    c.fillStyle='rgba(0,0,0,0.18)';
    [.62,.75,.88].forEach(function(fy){c.fillRect(cpx+cpw*.15,cpy+cph*fy,cpw*.7,cph*.08);});
    c.fillStyle=og;},
  coldmixer(c,s){
    // Cold Mixer — horizontal trough, twin helical screws, cooling jacket, motor right
    const{x,y,w,h}=s;const og=c.fillStyle;const midY=y+h/2;
    c.fillStyle='rgba(0,0,0,0.04)';c.fillRect(x-3,y-3,w+6,h+6);c.strokeRect(x-3,y-3,w+6,h+6);
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(x,y,w*.78,h);c.strokeRect(x,y,w*.78,h);
    var screwW=w*.74,sX=x+w*.02;
    [h*.28,h*.72].forEach(function(oy,idx){
      var sy=y+oy;c.beginPath();
      for(var i=0;i<=screwW;i+=2){
        var py=sy+Math.sin((i/screwW)*Math.PI*8+(idx*Math.PI))*(h*.16);
        i===0?c.moveTo(sX+i,py):c.lineTo(sX+i,py);
      }
      c.globalAlpha=.55;c.stroke();c.globalAlpha=1;
    });
    c.fillRect(x,y,w*.03,h);c.strokeRect(x,y,w*.03,h);
    c.fillRect(x+w*.75,y,w*.03,h);c.strokeRect(x+w*.75,y,w*.03,h);
    c.beginPath();c.moveTo(x+w*.15,y);c.lineTo(x+w*.15,y-8);c.stroke();
    c.beginPath();c.arc(x+w*.15,y-10,3.5,0,Math.PI*2);
    c.fillStyle='rgba(0,0,0,0.15)';c.fill();c.stroke();c.fillStyle=og;
    c.beginPath();c.moveTo(x+w*.6,y+h);c.lineTo(x+w*.6,y+h+8);c.stroke();
    var gbx=x+w*.78,gbw=w*.1,gbh=h*.55,gby=y+h*.22;
    c.fillRect(gbx,gby,gbw,gbh);c.strokeRect(gbx,gby,gbw,gbh);
    var mR=h*.24,mCX=gbx+gbw+mR+2,mCY=y+h*.5;
    c.beginPath();c.arc(mCX,mCY,mR,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();c.fillStyle=og;
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font='bold '+Math.max(6,mR*.7)+'px Segoe UI';c.textAlign='center';
    c.fillText('M',mCX,mCY+mR*.25);
    c.beginPath();c.moveTo(x+w*.78,midY);c.lineTo(gbx,midY);c.stroke();
    c.fillStyle=og;},
  buffertank(c,s){
    const{x,y,w,h}=s,r=w*.2;
    const og=c.fillStyle;
    c.fillRect(x,y+r,w,h-r*2);c.strokeRect(x,y+r,w,h-r*2);
    c.beginPath();c.ellipse(x+w/2,y+r,w/2,r,0,0,Math.PI*2);c.fill();c.stroke();
    c.beginPath();c.ellipse(x+w/2,y+h-r,w/2,r,0,0,Math.PI*2);c.fill();c.stroke();
    // fill level 65%
    c.fillStyle='rgba(0,0,0,0.06)';c.fillRect(x+1,y+h*.37,w-2,h*.6);c.fillStyle=og;
    // dashed level mark
    c.setLineDash([3,2]);c.globalAlpha=.5;
    c.beginPath();c.moveTo(x+3,y+h*.37);c.lineTo(x+w-3,y+h*.37);c.stroke();
    c.setLineDash([]);c.globalAlpha=1;
    // nozzles
    c.beginPath();c.moveTo(x+w/2,y+r);c.lineTo(x+w/2,y);c.stroke();
    c.beginPath();c.moveTo(x+w/2,y+h-r);c.lineTo(x+w/2,y+h);c.stroke();
    c.beginPath();c.moveTo(x+w,y+h*.5);c.lineTo(x+w+8,y+h*.5);c.stroke();
    c.fillStyle=og;},

  centdryer(c,s){
    const{x,y,w,h}=s,cx=x+w/2,cy=y+h/2,r=Math.min(w,h)*.42;
    c.beginPath();c.arc(cx,cy,r,0,Math.PI*2);c.fill();c.stroke();
    c.setLineDash([3,2]);
    c.beginPath();c.arc(cx,cy,r*.62,0,Math.PI*2);c.globalAlpha=.5;c.stroke();c.globalAlpha=1;
    c.setLineDash([]);
    for(var i=0;i<4;i++){
      var a=i*Math.PI/2;
      c.beginPath();c.moveTo(cx,cy);
      c.quadraticCurveTo(cx+Math.cos(a+.7)*r*.45,cy+Math.sin(a+.7)*r*.45,cx+Math.cos(a)*r*.62,cy+Math.sin(a)*r*.62);
      c.globalAlpha=.5;c.stroke();c.globalAlpha=1;
    }
    c.beginPath();c.moveTo(cx,cy-r);c.lineTo(cx,cy-r-8);c.stroke();
    c.beginPath();c.moveTo(cx-6,cy+r);c.lineTo(cx-6,cy+r+8);c.stroke();
    c.beginPath();c.moveTo(cx+6,cy+r);c.lineTo(cx+6,cy+r+8);c.stroke();
    var og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font='bold 7px Segoe UI';c.textAlign='center';c.fillText('CD',cx,cy+3);c.fillStyle=og;},

  vibroscreen(c,s){
    const{x,y,w,h}=s;
    c.save();c.translate(x+w/2,y+h/2);c.rotate(-0.1);
    c.fillRect(-w/2,-h/2,w,h);c.strokeRect(-w/2,-h/2,w,h);
    for(var i=1;i<5;i++){c.beginPath();c.moveTo(-w/2+w*i/5,-h/2);c.lineTo(-w/2+w*i/5,h/2);c.globalAlpha=.25;c.stroke();c.globalAlpha=1;}
    for(var j=1;j<3;j++){c.beginPath();c.moveTo(-w/2,-h/2+h*j/3);c.lineTo(w/2,-h/2+h*j/3);c.globalAlpha=.25;c.stroke();c.globalAlpha=1;}
    c.restore();
    var og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font='9px Segoe UI';c.textAlign='center';c.fillText('≈≈',x+w/2,y+h/2+3);c.fillStyle=og;},

  dedusting(c,s){
    const{x,y,w,h}=s,bH=h*.72;
    c.fillRect(x,y,w,bH);c.strokeRect(x,y,w,bH);
    c.beginPath();c.ellipse(x+w/2,y,w/2,5,0,0,Math.PI*2);c.stroke();
    var og=c.fillStyle;c.fillStyle='rgba(0,0,0,0.08)';
    [[x+w*.18,y+bH*.08],[x+w*.48,y+bH*.08],[x+w*.18,y+bH*.44],[x+w*.48,y+bH*.44]].forEach(function(p){
      c.beginPath();c.roundRect(p[0],p[1],w*.25,bH*.3,3);c.fill();c.stroke();
    });
    c.fillStyle=og;
    c.beginPath();c.moveTo(x+w/2,y);c.lineTo(x+w/2,y-8);c.stroke();
    c.beginPath();c.moveTo(x,y+bH);c.lineTo(x+w/2,y+h);c.lineTo(x+w,y+bH);c.closePath();c.fill();c.stroke();},

  butterflyvalve(c,s){
    const{x,y,w,h}=s,cx=x+w/2,cy=y+h/2,r=Math.min(w,h)*.44;
    c.beginPath();c.moveTo(x,cy);c.lineTo(x+w,cy);c.stroke();
    c.beginPath();c.arc(cx,cy,r,0,Math.PI*2);c.fill();c.stroke();
    // disc line diagonal
    c.lineWidth=(s.strokeWidth||1.5)*2;
    c.beginPath();c.moveTo(cx-r*.72,cy-r*.52);c.lineTo(cx+r*.72,cy+r*.52);c.stroke();
    c.lineWidth=s.strokeWidth||1.5;
    // actuator stem
    c.beginPath();c.moveTo(cx,cy-r);c.lineTo(cx,cy-r-7);c.stroke();
    var og=c.fillStyle;c.fillStyle='#888';
    c.beginPath();c.arc(cx,cy-r-10,3.5,0,Math.PI*2);c.fill();c.stroke();c.fillStyle=og;},

  solenoidvalve(c,s){
    const{x,y,w,h}=s;
    c.beginPath();c.moveTo(x,y+h*.6);c.lineTo(x+w,y+h*.6);c.stroke();
    c.beginPath();c.moveTo(x,y+h*.35);c.lineTo(x+w,y+h*.75);c.lineTo(x+w,y+h*.35);c.lineTo(x,y+h*.75);c.closePath();c.fill();c.stroke();
    c.fillRect(x+w*.15,y,w*.7,h*.35);c.strokeRect(x+w*.15,y,w*.7,h*.35);
    for(var i=1;i<4;i++){c.beginPath();c.moveTo(x+w*.15+w*.7*i/4,y);c.lineTo(x+w*.15+w*.7*i/4,y+h*.35);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;}},

  levelswitch(c,s){
    const{x,y,w,h}=s;
    var og=c.fillStyle;
    c.strokeRect(x+w*.3,y,w*.4,h);c.fillRect(x+w*.3,y,w*.4,h);
    c.fillStyle='rgba(41,128,185,0.35)';c.fillRect(x+w*.3+1,y+h*.55,w*.4-2,h*.44);
    c.fillStyle=og;
    c.beginPath();c.moveTo(x,y+h*.55);c.lineTo(x+w*.3,y+h*.55);c.stroke();
    c.beginPath();c.moveTo(x+w*.3,y+h*.55);c.lineTo(x,y+h*.4);c.stroke();
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font='6px Segoe UI';c.textAlign='center';
    c.fillText('LS',x+w/2,y+h*.45);c.fillStyle=og;},

  vacpump(c,s){
    const{x,y,w,h}=s,cx=x+w/2,cy=y+h/2,r=Math.min(w,h)*.4;
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    c.beginPath();c.arc(cx,cy,r*.72,0,Math.PI*2);c.stroke();
    c.beginPath();c.moveTo(x,cy);c.lineTo(x+w*.28,cy);c.stroke();
    c.beginPath();c.moveTo(x+w,cy);c.lineTo(x+w*.72,cy);c.stroke();
    var og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font='6px Segoe UI';c.textAlign='center';c.fillText('VAC',cx,cy+3);c.fillStyle=og;},

  weighbatcher(c,s){
    const{x,y,w,h}=s,hw=w*.72,lx=x+(w-hw)/2,bH=h*.5;
    c.beginPath();c.moveTo(lx,y);c.lineTo(lx+hw,y);c.lineTo(lx+hw*.78,y+bH);c.lineTo(lx+hw*.22,y+bH);c.closePath();c.fill();c.stroke();
    [lx+hw*.14,lx+hw*.86].forEach(function(px){
      c.beginPath();c.moveTo(px,y+bH);c.lineTo(px,y+h*.78);c.stroke();
      var og=c.fillStyle;c.fillStyle='#888';c.beginPath();c.arc(px,y+h*.81,4,0,Math.PI*2);c.fill();c.stroke();c.fillStyle=og;
    });
    c.fillRect(x+w*.08,y+h*.84,w*.84,h*.16);c.strokeRect(x+w*.08,y+h*.84,w*.84,h*.16);
    var og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font='5.5px Segoe UI';c.textAlign='center';c.fillText('kg',x+w/2,y+h*.96);c.fillStyle=og;},

  airfilter(c,s){
    const{x,y,w,h}=s,bH=h*.78;
    c.fillRect(x,y,w,bH);c.strokeRect(x,y,w,bH);
    c.beginPath();c.ellipse(x+w/2,y,w/2,5,0,0,Math.PI*2);c.stroke();
    c.setLineDash([2,2]);
    for(var i=1;i<5;i++){c.beginPath();c.moveTo(x,y+bH*i/5);c.lineTo(x+w,y+bH*i/5);c.globalAlpha=.3;c.stroke();c.globalAlpha=1;}
    c.setLineDash([]);
    c.beginPath();c.moveTo(x,y+bH);c.lineTo(x+w/2,y+h);c.lineTo(x+w,y+bH);c.closePath();c.fill();c.stroke();},

  nitrogen(c,s){
    const{x,y,w,h}=s,neck=w*.42,neckH=h*.18,bodyH=h-neckH,nl=(w-neck)/2;
    c.fillRect(x,y+neckH,w,bodyH);c.strokeRect(x,y+neckH,w,bodyH);
    c.fillRect(x+nl,y,neck,neckH);c.strokeRect(x+nl,y,neck,neckH);
    c.fillRect(x+nl*1.2,y-5,neck*.8,6);c.strokeRect(x+nl*1.2,y-5,neck*.8,6);
    var og=c.fillStyle;c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font='bold 9px Segoe UI';c.textAlign='center';c.fillText('N₂',x+w/2,y+neckH+bodyH*.45);
    c.font='6px Segoe UI';c.fillText('GAS',x+w/2,y+neckH+bodyH*.65);c.fillStyle=og;},

  bubblecolumn(c,s){
    // Bubble Column Reactor
    // Reference image (top → bottom):
    //   • Two feed pipes entering at top with ball-joint connectors
    //   • Tall narrow cylindrical vessel body
    //   • Rectangular sight-glass / temperature-gauge box (mid-upper)
    //   • Side liquid-level nozzle (mid)
    //   • Constricted waist / necked transition zone
    //   • Flat base plate / support flange
    //   • Motor + gear drive unit at very bottom
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const CX=x+w/2;
    const lw=s.strokeWidth||1.5;

    // ── TOP FEED PIPES (two diagonal lines spreading upward like a V) ──
    // Each pipe is a diagonal line from the vessel top-centre, fanning out
    const pipeBaseY=y+h*.06, pipeTopY=y-h*.05;
    const pipeSpread=w*.45;
    // Left pipe
    c.lineWidth=lw*1.4;
    c.beginPath();c.moveTo(CX-w*.10,pipeBaseY);c.lineTo(CX-pipeSpread,pipeTopY);c.stroke();
    // Right pipe
    c.beginPath();c.moveTo(CX+w*.10,pipeBaseY);c.lineTo(CX+pipeSpread,pipeTopY);c.stroke();
    c.lineWidth=lw;
    // Ball connectors at pipe tips (small filled circles)
    [[CX-pipeSpread,pipeTopY],[CX+pipeSpread,pipeTopY]].forEach(([bx,by])=>{
      c.beginPath();c.arc(bx,by,3.5,0,Math.PI*2);
      c.fillStyle=s.strokeColor||'#1a1a1a';c.fill();c.stroke();
      c.fillStyle=og;
    });

    // ── MAIN CYLINDRICAL VESSEL BODY (top cap → waist) ──
    const cylX=x+w*.15, cylW=w*.70;
    const cylTop=y+h*.05, cylBot=y+h*.68;
    const cylH=cylBot-cylTop;
    // Top ellipse cap
    c.beginPath();c.ellipse(CX,cylTop,cylW/2,h*.018,0,0,Math.PI*2);
    c.fillStyle=s.fillColor||'#ffffff';c.fill();c.stroke();c.fillStyle=og;
    // Cylinder walls
    c.fillRect(cylX,cylTop,cylW,cylH);
    c.strokeRect(cylX,cylTop,cylW,cylH);
    // Subtle bubble fill (light stipple to suggest aerated liquid)
    c.fillStyle='rgba(0,0,0,0.03)';
    c.fillRect(cylX+1,cylTop+cylH*.12,cylW-2,cylH*.82);
    c.fillStyle=og;
    // Small bubble dots inside vessel
    const bubblePositions=[[.25,.25],[.65,.22],[.45,.40],[.20,.55],[.70,.50],[.38,.68],[.60,.72],[.30,.82],[.55,.85]];
    c.fillStyle='rgba(0,0,0,0.18)';
    bubblePositions.forEach(([fx,fy])=>{
      const bR=Math.max(1.5,w*.025);
      c.beginPath();c.arc(cylX+cylW*fx,cylTop+cylH*fy,bR,0,Math.PI*2);c.fill();
    });
    c.fillStyle=og;

    // ── SIGHT-GLASS / TEMPERATURE NOZZLE BOX (upper-mid, left side) ──
    // A small rectangle with dot-array (6 dots in 2×3) on the left wall
    const sgW=w*.28, sgH=h*.10, sgX=cylX-sgW, sgY=cylTop+cylH*.15;
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(sgX,sgY,sgW,sgH);
    c.strokeRect(sgX,sgY,sgW,sgH);
    c.fillStyle=og;
    // Connection stub to vessel wall
    c.beginPath();c.moveTo(cylX,sgY+sgH/2);c.lineTo(sgX+sgW,sgY+sgH/2);c.stroke();
    // 6 dots inside box (2 cols × 3 rows)
    c.fillStyle=s.strokeColor||'#1a1a1a';
    [[.28,.25],[.72,.25],[.28,.55],[.72,.55],[.28,.82],[.72,.82]].forEach(([fx,fy])=>{
      c.beginPath();c.arc(sgX+sgW*fx,sgY+sgH*fy,1.8,0,Math.PI*2);c.fill();
    });
    c.fillStyle=og;

    // ── LIQUID-LEVEL NOZZLE (mid, right side) ──
    // Small square stub + horizontal connection line
    const llY=cylTop+cylH*.48, llW=w*.18, llH=h*.04;
    c.fillRect(cylX+cylW,llY,llW,llH);
    c.strokeRect(cylX+cylW,llY,llW,llH);
    // Green indicator line (matches reference green lines)
    c.strokeStyle='#00aa55';c.lineWidth=lw*1.1;
    c.beginPath();c.moveTo(cylX+cylW+llW,llY+llH/2);c.lineTo(cylX+cylW+llW+w*.12,llY+llH/2);c.stroke();
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=lw;

    // ── NECKED / CONSTRICTED WAIST TRANSITION ──
    const neckTopY=cylBot, neckBotY=y+h*.76, neckW=cylW*.52;
    c.beginPath();
    c.moveTo(cylX,neckTopY);
    c.lineTo(CX-neckW/2,neckBotY);
    c.lineTo(CX+neckW/2,neckBotY);
    c.lineTo(cylX+cylW,neckTopY);
    c.closePath();c.fill();c.stroke();

    // ── BASE FLANGE / SUPPORT PLATE ──
    const flH=h*.04, flW=w*.90, flY=neckBotY, flX=CX-flW/2;
    c.fillRect(flX,flY,flW,flH);
    c.strokeRect(flX,flY,flW,flH);
    // Second narrower flange below
    const fl2W=flW*.72, fl2Y=flY+flH, fl2X=CX-fl2W/2;
    c.fillRect(fl2X,fl2Y,fl2W,flH*.8);
    c.strokeRect(fl2X,fl2Y,fl2W,flH*.8);

    // ── MOTOR / DRIVE UNIT AT BOTTOM ──
    // Rectangular motor housing
    const mHW=flW*.55, mHH=h*.09, mHX=CX-mHW/2, mHY=fl2Y+flH*.8;
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(mHX,mHY,mHW,mHH);
    c.strokeRect(mHX,mHY,mHW,mHH);
    c.fillStyle=og;
    // Internal detail: two horizontal lines (finned / ribbed casing)
    c.globalAlpha=.3;
    [.35,.65].forEach(fy=>{
      c.beginPath();c.moveTo(mHX+2,mHY+mHH*fy);c.lineTo(mHX+mHW-2,mHY+mHH*fy);c.stroke();
    });
    c.globalAlpha=1;
    // Shaft coupling line going up into flange
    c.lineWidth=lw*1.5;
    c.beginPath();c.moveTo(CX,fl2Y);c.lineTo(CX,mHY);c.stroke();
    c.lineWidth=lw;
    // Drive shaft stub going right (output shaft)
    c.beginPath();c.moveTo(mHX+mHW,mHY+mHH/2);c.lineTo(mHX+mHW+w*.14,mHY+mHH/2);c.stroke();
    // Small coupling square at shaft end
    const cpS=mHH*.35, cpX=mHX+mHW+w*.14, cpY=mHY+mHH/2-cpS/2;
    c.fillRect(cpX,cpY,cpS,cpS);c.strokeRect(cpX,cpY,cpS,cpS);

    // Base ground line
    const gY=mHY+mHH+2;
    c.lineWidth=lw*1.8;
    c.beginPath();c.moveTo(mHX-4,gY);c.lineTo(mHX+mHW+w*.14+cpS+4,gY);c.stroke();
    c.lineWidth=lw;

    c.fillStyle=og;},

  // ══════ NEW JCC EQUIPMENT ══════

  transferconv(c,s){
    // Inclined belt conveyor (Transfer Conveyor) — angled frame with belt and rollers
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    const angle=0.28; // slight incline visual
    // Draw angled frame rails (top and bottom)
    const topY=y, botY=y+h*.5, rightOffY=h*.35;
    c.fillStyle=s.fillColor||'#ffffff';
    // Main belt body (parallelogram)
    c.beginPath();
    c.moveTo(x,y+h*.35);c.lineTo(x+w,y);
    c.lineTo(x+w,y+h*.45);c.lineTo(x,y+h*.8);
    c.closePath();c.fill();c.stroke();
    // Rollers at each end
    const rR=h*.12;
    c.beginPath();c.arc(x+rR*1.2,y+h*.575,rR,0,Math.PI*2);c.stroke();
    c.beginPath();c.arc(x+w-rR*1.2,y+h*.22,rR,0,Math.PI*2);c.stroke();
    // Support legs (two vertical lines at each end)
    c.globalAlpha=.5;
    c.beginPath();c.moveTo(x+rR*1.2,y+h*.575+rR);c.lineTo(x+rR*1.2,y+h);c.stroke();
    c.beginPath();c.moveTo(x+w-rR*1.2,y+h*.22+rR);c.lineTo(x+w-rR*1.2,y+h*.75);c.stroke();
    c.globalAlpha=1;
    // Belt surface lines (showing slats/cleats)
    c.globalAlpha=.25;
    for(let i=1;i<5;i++){
      const t=i/5;
      const sx1=x+(w-rR*2.4)*t+rR*1.2, sy1=y+h*.35-(h*.35)*t+h*.35+(h*.45-h*.35)*t-h*.12*t;
      // simple diagonal lines across belt width
      const bx=x+w*t;
      const by=y+h*.35*(1-t)+h*.0*t;
      c.beginPath();c.moveTo(bx,by+3);c.lineTo(bx,by+h*.32);c.stroke();
    }
    c.globalAlpha=1;
    c.fillStyle=og;},

  crusherconv(c,s){
    // Crusher product conveyor — steeper incline, chain/slat conveyor look
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    c.fillStyle=s.fillColor||'#ffffff';
    // Main angled trough body
    c.beginPath();
    c.moveTo(x,y+h*.5);c.lineTo(x+w,y+h*.08);
    c.lineTo(x+w,y+h*.55);c.lineTo(x,y+h*.97);
    c.closePath();c.fill();c.stroke();
    // Drive rollers
    const rR=h*.13;
    c.beginPath();c.arc(x+rR*1.4,y+h*.735,rR,0,Math.PI*2);c.stroke();
    c.beginPath();c.arc(x+w-rR*1.4,y+h*.315,rR,0,Math.PI*2);c.stroke();
    // Slat/chain detail lines
    c.globalAlpha=.28;
    for(let i=1;i<6;i++){
      const t=i/6;
      const bx=x+w*t;
      const by=y+h*.5-h*.42*t;
      c.beginPath();c.moveTo(bx-2,by+4);c.lineTo(bx-2,by+h*.38);c.stroke();
    }
    c.globalAlpha=1;
    // Crusher label "CR" on left
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.min(9,h*.18)}px Segoe UI`;c.textAlign='center';
    c.fillText('CR',x+w*.12,y+h*.82);
    c.fillStyle=og;},

  washcontainer(c,s){
    // Washing container — rectangular tank with wave/water lines and inlet/outlet pipes
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // Outer vessel
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(x,y+h*.12,w,h*.76);c.strokeRect(x,y+h*.12,w,h*.76);
    // Water fill (light blue tint area)
    c.fillStyle='rgba(0,120,212,0.08)';
    c.fillRect(x+1,y+h*.35,w-2,h*.53-1);
    // Wave lines (water surface)
    c.strokeStyle='rgba(0,120,212,0.5)';c.lineWidth=1;
    c.beginPath();
    for(let i=0;i<=w-4;i+=2){
      const wy=y+h*.38+Math.sin(i/((w-4)/3)*Math.PI)*(h*.025);
      i===0?c.moveTo(x+2+i,wy):c.lineTo(x+2+i,wy);}
    c.stroke();
    c.beginPath();
    for(let i=0;i<=w-4;i+=2){
      const wy=y+h*.5+Math.sin((i/(w-4)*3+1)*Math.PI)*(h*.022);
      i===0?c.moveTo(x+2+i,wy):c.lineTo(x+2+i,wy);}
    c.stroke();
    c.strokeStyle=s.strokeColor||'#1a1a1a';c.lineWidth=s.strokeWidth||1.5;
    // Top inlet pipe (center top)
    c.beginPath();c.moveTo(x+w/2-5,y);c.lineTo(x+w/2-5,y+h*.12);c.moveTo(x+w/2+5,y);c.lineTo(x+w/2+5,y+h*.12);c.stroke();
    // Bottom discharge pipe
    c.beginPath();c.moveTo(x+w*.35,y+h*.88);c.lineTo(x+w*.35,y+h);c.moveTo(x+w*.65,y+h*.88);c.lineTo(x+w*.65,y+h);c.stroke();
    // Label "WASH" inside
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.min(8,w*.11)}px Segoe UI`;c.textAlign='center';
    c.fillText('WASH',x+w/2,y+h*.28);
    c.fillStyle=og;},

  drytank(c,s){
    // Dry tank — sealed vertical vessel with air vent on top and drain below
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    c.fillStyle=s.fillColor||'#ffffff';
    // Vessel body
    c.fillRect(x,y+h*.1,w,h*.75);c.strokeRect(x,y+h*.1,w,h*.75);
    // Domed top
    c.beginPath();c.ellipse(x+w/2,y+h*.1,w/2,h*.1,0,Math.PI,0);c.fill();c.stroke();
    // Conical/sloped bottom
    c.beginPath();c.moveTo(x,y+h*.85);c.lineTo(x+w/2,y+h*.96);c.lineTo(x+w,y+h*.85);c.closePath();c.fill();c.stroke();
    // Vertical internal baffles (aeration grid)
    c.globalAlpha=.2;
    for(let i=1;i<4;i++){const bx=x+w*i/4;c.beginPath();c.moveTo(bx,y+h*.12);c.lineTo(bx,y+h*.84);c.stroke();}
    c.globalAlpha=1;
    // Vent nozzle on top
    c.beginPath();c.moveTo(x+w/2,y+h*.0);c.lineTo(x+w/2,y-h*.04);c.stroke();
    c.beginPath();c.moveTo(x+w/2-5,y-h*.04);c.lineTo(x+w/2+5,y-h*.04);c.stroke();
    // Drain nozzle bottom
    c.beginPath();c.moveTo(x+w/2,y+h*.96);c.lineTo(x+w/2,y+h);c.stroke();
    // Level indicator line (dashed)
    c.setLineDash([3,2]);c.globalAlpha=.3;
    c.beginPath();c.moveTo(x+3,y+h*.6);c.lineTo(x+w-3,y+h*.6);c.stroke();
    c.setLineDash([]);c.globalAlpha=1;
    // Label "DRY TANK"
    c.fillStyle=s.strokeColor||'#1a1a1a';
    c.font=`bold ${Math.min(7,w*.1)}px Segoe UI`;c.textAlign='center';
    c.fillText('DRY',x+w/2,y+h*.45);c.fillText('TANK',x+w/2,y+h*.56);
    c.fillStyle=og;},

  basketconv(c,s){
    // Basket / Basket-link conveyor — horizontal conveyor with basket/bucket links
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    c.fillStyle=s.fillColor||'#ffffff';
    // Main frame body
    c.fillRect(x,y,w,h);c.strokeRect(x,y,w,h);
    // End drums/sprockets
    const rR=h*.35;
    c.beginPath();c.arc(x+rR,y+h/2,rR,0,Math.PI*2);c.stroke();
    c.beginPath();c.arc(x+w-rR,y+h/2,rR,0,Math.PI*2);c.stroke();
    // Chain belt lines (top and bottom runs)
    c.globalAlpha=.4;
    c.beginPath();c.moveTo(x+rR,y+h*.18);c.lineTo(x+w-rR,y+h*.18);c.stroke();
    c.beginPath();c.moveTo(x+rR,y+h*.82);c.lineTo(x+w-rR,y+h*.82);c.stroke();
    c.globalAlpha=1;
    // Basket/bucket detail shapes evenly along top run
    const N=4;
    for(let i=0;i<N;i++){
      const bx=x+rR*1.8+i*((w-rR*3.6)/(N-1));
      c.beginPath();
      c.moveTo(bx-6,y+h*.18);c.lineTo(bx-8,y+h*.5);c.lineTo(bx+8,y+h*.5);c.lineTo(bx+6,y+h*.18);
      c.closePath();c.globalAlpha=.45;c.fill();c.stroke();c.globalAlpha=1;
    }
    c.fillStyle=og;},

  sidefeeder(c,s){
    // Side feeder — twin screw feeder mounted perpendicular to main extruder, with stand
    const{x,y,w,h}=s;
    const og=c.fillStyle;
    // Motor housing (top-left box)
    const mW=w*.42,mH=h*.28,mX=x,mY=y;
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(mX,mY,mW,mH);c.strokeRect(mX,mY,mW,mH);
    // Motor M circle inside
    c.beginPath();c.arc(mX+mW*.65,mY+mH/2,mH*.36,0,Math.PI*2);c.stroke();
    const fsave=c.fillStyle;
    c.fillStyle=s.strokeColor||'#1a1a1a';c.font=`bold ${Math.min(8,mH*.55)}px Segoe UI`;c.textAlign='center';
    c.fillText('M',mX+mW*.65,mY+mH/2+3);
    // Gearbox (trapezoid below motor)
    const gW=w*.3,gH=h*.22,gX=x+w*.06,gY=mY+mH;
    c.fillStyle=s.fillColor||'#ffffff';
    c.beginPath();c.moveTo(gX,gY);c.lineTo(gX+gW,gY);c.lineTo(gX+gW*.85,gY+gH);c.lineTo(gX+gW*.15,gY+gH);c.closePath();c.fill();c.stroke();
    // Barrel (horizontal — side feed direction)
    const bW=w*.72,bH=h*.22,bX=x+w*.28,bY=gY+gH*.2;
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(bX,bY,bW,bH);c.strokeRect(bX,bY,bW,bH);
    // Twin screw helices inside barrel
    [.38,.62].forEach((oy,idx)=>{
      c.beginPath();
      for(let i=0;i<=bW;i+=2){const hy=bY+bH*oy+Math.sin((i/bW*Math.PI*5)+(idx*Math.PI))*(bH*.18);i===0?c.moveTo(bX+i,hy):c.lineTo(bX+i,hy);}
      c.globalAlpha=.35;c.stroke();c.globalAlpha=1;
    });
    // Mounting stand / base plate
    const stX=x+w*.12,stY=bY+bH,stW=w*.5,stH=h*.14;
    c.fillStyle='rgba(0,0,0,0.08)';
    c.fillRect(stX,stY,stW,stH);c.strokeRect(stX,stY,stW,stH);
    // Vertical column
    c.fillStyle=s.fillColor||'#ffffff';
    c.fillRect(stX+stW*.38,bY+bH,stW*.24,stH);c.strokeRect(stX+stW*.38,bY+bH,stW*.24,stH);
    // Discharge end cap (right)
    c.beginPath();c.arc(bX+bW+6,bY+bH/2,bH*.35,0,Math.PI*2);c.fill();c.stroke();
    // Wheel / caster circles at base (two)
    [.28,.68].forEach(wx=>{
      c.beginPath();c.arc(x+w*wx,y+h,h*.07,0,Math.PI*2);c.stroke();
    });
    c.fillStyle=og;},
};

// ─── Dispatch: style the context, then draw the shape (or a labelled fallback) ───
export function drawFn(c: CanvasRenderingContext2D, s: Shape): void {
  setCtx(c, s);
  const fn = D[s.type];
  if (fn) {
    fn(c, s);
  } else {
    c.fillRect(s.x, s.y, s.w, s.h);
    c.strokeRect(s.x, s.y, s.w, s.h);
    const og = c.fillStyle;
    c.fillStyle = s.strokeColor || '#1a1a1a';
    c.font = `bold ${Math.min(10, s.h * 0.3)}px Segoe UI`;
    c.textAlign = 'center';
    c.fillText((s.type || '').toUpperCase().slice(0, 8), s.x + s.w / 2, s.y + s.h / 2 + 4);
    c.fillStyle = og;
  }
}

// ─── Draw a shape's text label + optional P&ID tag pill ───
export function drawLabel(c: CanvasRenderingContext2D, s: Shape): void {
  if (!s.label || s.type === 'textlabel' || s.type === 'zonebox' || s.type === 'titleblock') return;
  const fs = s.fontSize || 10;
  c.font = `${fs}px Segoe UI`;
  c.fillStyle = s.strokeColor || '#1a1a1a';
  c.textAlign = 'center';
  const lp = s.labelPos || 'bottom';
  let lx = s.x + s.w / 2;
  let ly = 0;
  if (lp === 'bottom') ly = s.y + s.h + fs + 2;
  else if (lp === 'top') ly = s.y - 4;
  else if (lp === 'center') {
    ly = s.y + s.h / 2 + fs * 0.35;
  } else if (lp === 'left') {
    lx = s.x - 4;
    ly = s.y + s.h / 2 + fs * 0.35;
    c.textAlign = 'right';
  } else {
    lx = s.x + s.w + 4;
    ly = s.y + s.h / 2 + fs * 0.35;
    c.textAlign = 'left';
  }
  c.fillText(s.label, lx, ly);
  if (s.tag) {
    const og = c.fillStyle;
    c.fillStyle = '#e67e22';
    c.font = `bold ${Math.max(7, fs - 2)}px Segoe UI`;
    c.textAlign = 'center';
    c.fillText(s.tag, s.x + s.w / 2, s.y - 14);
    c.fillStyle = og;
  }
}
